import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Alert,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Sports as SportsIcon,
  CalendarToday as CalendarIcon,
  Group as GroupIcon,
  PlayArrow as PlayIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Shuffle as ShuffleIcon,
  Print as PrintIcon,
  WhatsApp as WhatsAppIcon,
  AddAlarm as AddAlarmIcon,
  Edit as EditIcon,
  RemoveCircle as RemoveIcon,
  Sort as SortIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  PersonAdd as PersonAddIcon,
  DragIndicator as DragIcon,
  FormatListNumbered as PlayerListIcon,
  ContentCopy as CopyIcon,
  EventBusy as CancelDayIcon,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Player, LeagueDay, RandomGenerationResult, Division } from "../types";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../config/api";
import RandomGeneratorDialog from "../components/RandomGeneratorDialog";
import HistoricalLeagueDayDialog from "../components/HistoricalLeagueDayDialog";
import {
  generateRandomPlayerAssignments,
  generateMatchPattern,
} from "../utils/randomGenerator";
import { useDivision } from "../contexts/DivisionContext";
import { useSeason } from "../contexts/SeasonContext";
import DivisionSelector from "../components/DivisionSelector";
import SeasonSelector from "../components/SeasonSelector";
import VenueSelector from "../components/VenueSelector";
import VenueBadge from "../components/VenueBadge";
import { getMapsUrl } from "../utils/venue";
import { formatLeagueDayDate, formatLeagueDayDateShort, formatLeagueDayDateCustom } from "../utils/dateFormat";

interface CrossDivisionSelection {
  id: number;
  division: Division | "";
  playerId: string;
}

function LeagueDays() {
  const { currentDivision, allDivisions, setCurrentDivision } = useDivision();
  const { selectedSeason } = useSeason();
  const [leagueDays, setLeagueDays] = useState<LeagueDay[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]); // All players across divisions
  const [crossDivisionSelections, setCrossDivisionSelections] = useState<
    CrossDivisionSelection[]
  >([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historicalDialogOpen, setHistoricalDialogOpen] = useState(false);
  const [randomDialogOpen, setRandomDialogOpen] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null); // Track if we're editing a draft
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [leagueDayName, setLeagueDayName] = useState<string>("");
  const [assignedCourt, setAssignedCourt] = useState<number | null>(null);
  const [availableCourtBookings, setAvailableCourtBookings] = useState<any[]>([]);
  const [allCourtBookings, setAllCourtBookings] = useState<any[]>([]); // All court bookings for info display
  const [selectedCourtBookingId, setSelectedCourtBookingId] = useState<string>("");
  const [selectedVenueId, setSelectedVenueId] = useState<string>("");
  const [courtCount, setCourtCount] = useState<number>(
    currentDivision === "Master" ? 2 : 3,
  );
  const [playerAssignments, setPlayerAssignments] =
    useState<RandomGenerationResult | null>(null);
  // Lives here (not inside RandomGeneratorDialog) so Cancel + reopening the dialog keeps
  // counting attempts for the same league day instead of resetting to 0.
  const [generationCount, setGenerationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [leagueDayToCancel, setLeagueDayToCancel] = useState<LeagueDay | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leagueDayToDelete, setLeagueDayToDelete] = useState<LeagueDay | null>(
    null,
  );
  const [recalculateDialogOpen, setRecalculateDialogOpen] = useState(false);
  const [leagueDayToRecalculate, setLeagueDayToRecalculate] =
    useState<LeagueDay | null>(null);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [leagueDayToRegenerate, setLeagueDayToRegenerate] =
    useState<LeagueDay | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [courtNumbersDialogOpen, setCourtNumbersDialogOpen] = useState(false);
  const [leagueDayToPrint, setLeagueDayToPrint] = useState<LeagueDay | null>(
    null,
  );
  const [customCourtNumbers, setCustomCourtNumbers] = useState<{
    [key: number]: string;
  }>({});
  const [skipPlayerSelection, setSkipPlayerSelection] = useState(false); // For creating draft without players
  const [isTrialDay, setIsTrialDay] = useState(false); // For creating trial days (point-based scoring, no stats)
  const [openRegistrationDialogOpen, setOpenRegistrationDialogOpen] =
    useState(false);
  const [scheduleRegistrationDialogOpen, setScheduleRegistrationDialogOpen] =
    useState(false);
  const [registrationListDialogOpen, setRegistrationListDialogOpen] =
    useState(false);
  const [leagueDayForRegistration, setLeagueDayForRegistration] =
    useState<LeagueDay | null>(null);
  const [playerLimit, setPlayerLimit] = useState<number>(12);
  const [registrationSortType, setRegistrationSortType] = useState<
    "order" | "alphabetical"
  >("order");
  const [selectedPlayerToAdd, setSelectedPlayerToAdd] = useState<Player | null>(
    null,
  );
  const [addToWaitlist, setAddToWaitlist] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState<Date | null>(null);
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [leagueDayForTime, setLeagueDayForTime] = useState<LeagueDay | null>(
    null,
  );
  const [timeInput, setTimeInput] = useState<string>("");
  const [timeLimitInput, setTimeLimitInput] = useState<string>("12");
  const [timeNameInput, setTimeNameInput] = useState<string>("");
  const [timeDateInput, setTimeDateInput] = useState<string>("");
  const [timeVenueId, setTimeVenueId] = useState<string>("");
  // Date view filter – shows all divisions' league days for a specific date
  const [dateViewFilter, setDateViewFilter] = useState<string>("");
  const [dateViewLeagueDays, setDateViewLeagueDays] = useState<LeagueDay[]>([]);
  const [dateViewLoading, setDateViewLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"division" | "date">("division");
  const [playerListDialogOpen, setPlayerListDialogOpen] = useState(false);
  // Trial day ranking
  const [trialRankingDialogOpen, setTrialRankingDialogOpen] = useState(false);
  const [trialRankingLeagueDay, setTrialRankingLeagueDay] = useState<LeagueDay | null>(null);
  const [trialRanking, setTrialRanking] = useState<{ playerId: string; name: string; points: number }[]>([]);
  const navigate = useNavigate();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Drag handlers for confirmed and waitlist
  const handleConfirmedDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !leagueDayForRegistration) return;

    if (active.id !== over.id) {
      const oldIndex =
        leagueDayForRegistration.selectedPlayers?.findIndex(
          (id) => id === active.id,
        ) ?? -1;
      const newIndex =
        leagueDayForRegistration.selectedPlayers?.findIndex(
          (id) => id === over.id,
        ) ?? -1;

      if (
        oldIndex !== -1 &&
        newIndex !== -1 &&
        leagueDayForRegistration.selectedPlayers
      ) {
        const reorderedIds = arrayMove(
          leagueDayForRegistration.selectedPlayers,
          oldIndex,
          newIndex,
        );

        // Optimistically update UI
        setLeagueDayForRegistration({
          ...leagueDayForRegistration,
          selectedPlayers: reorderedIds,
        });

        // Save to backend
        try {
          await apiRequest(
            `/api/league-days/${leagueDayForRegistration.id}/admin-reorder-registration`,
            {
              method: "POST",
              body: JSON.stringify({
                reorderedPlayerIds: reorderedIds,
                listType: "confirmed",
              }),
            },
          );
          await fetchLeagueDays();
        } catch (error) {
          console.error("Error reordering confirmed players:", error);
          // Revert on error
          await fetchLeagueDays();
          const updatedDay = leagueDays.find(
            (d) => d.id === leagueDayForRegistration.id,
          );
          if (updatedDay) setLeagueDayForRegistration(updatedDay);
        }
      }
    }
  };

  const handleWaitlistDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !leagueDayForRegistration) return;

    if (active.id !== over.id) {
      const oldIndex =
        leagueDayForRegistration.waitlistPlayerIds?.findIndex(
          (id) => id === active.id,
        ) ?? -1;
      const newIndex =
        leagueDayForRegistration.waitlistPlayerIds?.findIndex(
          (id) => id === over.id,
        ) ?? -1;

      if (
        oldIndex !== -1 &&
        newIndex !== -1 &&
        leagueDayForRegistration.waitlistPlayerIds
      ) {
        const reorderedIds = arrayMove(
          leagueDayForRegistration.waitlistPlayerIds,
          oldIndex,
          newIndex,
        );

        // Optimistically update UI
        setLeagueDayForRegistration({
          ...leagueDayForRegistration,
          waitlistPlayerIds: reorderedIds,
        });

        // Save to backend
        try {
          await apiRequest(
            `/api/league-days/${leagueDayForRegistration.id}/admin-reorder-registration`,
            {
              method: "POST",
              body: JSON.stringify({
                reorderedPlayerIds: reorderedIds,
                listType: "waitlist",
              }),
            },
          );
          await fetchLeagueDays();
        } catch (error) {
          console.error("Error reordering waitlist:", error);
          // Revert on error
          await fetchLeagueDays();
          const updatedDay = leagueDays.find(
            (d) => d.id === leagueDayForRegistration.id,
          );
          if (updatedDay) setLeagueDayForRegistration(updatedDay);
        }
      }
    }
  };

  useEffect(() => {
    fetchLeagueDays();
    fetchPlayers();
    fetchAllPlayers(); // Fetch all players for cross-division selection
    fetchAllCourtBookings(); // Fetch all court bookings for info display
    // Reset court count based on division default
    setCourtCount(currentDivision === "Master" ? 2 : 3);
    setSelectedPlayers([]);
    setCrossDivisionSelections([]);
  }, [currentDivision, selectedSeason]);

  // Fetch court bookings when date changes
  useEffect(() => {
    if (selectedDate && dialogOpen) {
      fetchCourtBookingsForDate(selectedDate);
    }
  }, [selectedDate, dialogOpen]);

  const fetchLeagueDays = async () => {
    if (!selectedSeason) return;

    try {
      const params = new URLSearchParams({
        division: currentDivision,
        seasonId: selectedSeason.id,
        includeCancelled: "true",
      });

      const response = await apiRequest(
        `/api/league-days?${params.toString()}`,
      );
      if (response.ok) {
        const data = await response.json();
        setLeagueDays(data);
      }
    } catch (error) {
      console.error("Error fetching league days:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeagueDaysByDate = async (dateStr: string) => {
    if (!selectedSeason || !dateStr) return;
    setDateViewLoading(true);
    try {
      // Fetch all league days for the season (no date filter) and filter client-side.
      // This ensures date comparison uses the same UTC logic as the date display formatter.
      const params = new URLSearchParams({ seasonId: selectedSeason.id });
      const response = await apiRequest(`/api/league-days?${params.toString()}`);
      if (response.ok) {
        const all: LeagueDay[] = await response.json();
        const [year, month, day] = dateStr.split("-").map(Number);
        const filtered = all.filter((ld) => {
          const d = new Date(ld.date);
          return (
            d.getUTCFullYear() === year &&
            d.getUTCMonth() + 1 === month &&
            d.getUTCDate() === day
          );
        });
        setDateViewLeagueDays(filtered);
      }
    } catch (error) {
      console.error("Error fetching league days by date:", error);
    } finally {
      setDateViewLoading(false);
    }
  };

  // Re-fetch date view when filter or season changes
  useEffect(() => {
    if (dateViewFilter) {
      fetchLeagueDaysByDate(dateViewFilter);
    } else {
      setDateViewLeagueDays([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateViewFilter, selectedSeason]);

  const fetchPlayers = async () => {
    try {
      const response = await apiRequest(
        `/api/players?division=${currentDivision}`,
      );
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      }
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const fetchAllPlayers = async () => {
    try {
      const response = await apiRequest(`/api/players`); // Fetch all players without division filter
      if (response.ok) {
        const data = await response.json();
        setAllPlayers(data);
      }
    } catch (error) {
      console.error("Error fetching all players:", error);
    }
  };

  const fetchAllCourtBookings = async () => {
    try {
      const response = await apiRequest('/api/court-bookings');
      if (response.ok) {
        const bookings = await response.json();
        setAllCourtBookings(bookings);
      } else {
        setAllCourtBookings([]);
      }
    } catch (error) {
      console.error("Error fetching all court bookings:", error);
      setAllCourtBookings([]);
    }
  };

  const getCourtBookingInfo = (leagueDay: LeagueDay): string | null => {
    if (!leagueDay.date) return null;
    
    const leagueDayDate = leagueDay.date instanceof Date 
      ? leagueDay.date 
      : new Date(leagueDay.date);
    const dateStr = leagueDayDate.toISOString().split('T')[0];
    
    // Find bookings for this date
    const dayBookings = allCourtBookings.filter((booking: any) => {
      const bookingDateStr = typeof booking.date === 'string'
        ? booking.date.split('T')[0]
        : new Date(booking.date).toISOString().split('T')[0];
      return bookingDateStr === dateStr;
    });

    if (dayBookings.length === 0) return null;

    // Find slots assigned to this division
    const assignedSlots: { court: number; time: string }[] = [];
    
    dayBookings.forEach(booking => {
      if (booking.courtSlots) {
        Object.entries(booking.courtSlots).forEach(([courtNum, slots]: [string, any]) => {
          (slots as any[]).forEach((slot: any) => {
            if (slot.division === leagueDay.division) {
              assignedSlots.push({
                court: parseInt(courtNum),
                time: `${slot.slotStartTime}-${slot.slotEndTime}`
              });
            }
          });
        });
      }
    });

    if (assignedSlots.length === 0) return null;

    // Group by time
    const timeGroups = assignedSlots.reduce((acc, slot) => {
      if (!acc[slot.time]) acc[slot.time] = [];
      acc[slot.time].push(slot.court);
      return acc;
    }, {} as { [time: string]: number[] });

    // Format output
    const parts = Object.entries(timeGroups).map(([time, courts]) => {
      const sortedCourts = courts.sort((a, b) => a - b);
      return `${time}: Court${courts.length > 1 ? 's' : ''} ${sortedCourts.join(', ')}`;
    });

    return parts.join(' | ');
  };

  const fetchCourtBookingsForDate = async (date: Date | string) => {
    try {
      const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      const response = await apiRequest(`/api/court-bookings/date/${dateStr}`);
      if (response.ok) {
        const bookings = await response.json();
        setAvailableCourtBookings(bookings);
      } else {
        setAvailableCourtBookings([]);
      }
    } catch (error) {
      console.error("Error fetching court bookings:", error);
      setAvailableCourtBookings([]);
    }
  };

  const handleCrossDivisionAdd = () => {
    const newId =
      crossDivisionSelections.length > 0
        ? Math.max(...crossDivisionSelections.map((s) => s.id)) + 1
        : 1;
    setCrossDivisionSelections([
      ...crossDivisionSelections,
      { id: newId, division: "", playerId: "" },
    ]);
  };

  const handleCrossDivisionRemove = (id: number) => {
    const selection = crossDivisionSelections.find((s) => s.id === id);
    if (selection && selection.playerId) {
      // Remove from selected players
      setSelectedPlayers((prev) =>
        prev.filter((pid) => pid !== selection.playerId),
      );
    }
    setCrossDivisionSelections(
      crossDivisionSelections.filter((s) => s.id !== id),
    );
  };

  const handleCrossDivisionDivisionChange = (
    id: number,
    division: Division | "",
  ) => {
    setCrossDivisionSelections(
      crossDivisionSelections.map((s) =>
        s.id === id ? { ...s, division, playerId: "" } : s,
      ),
    );
    // Remove any previously selected player from this slot
    const selection = crossDivisionSelections.find((s) => s.id === id);
    if (selection && selection.playerId) {
      setSelectedPlayers((prev) =>
        prev.filter((pid) => pid !== selection.playerId),
      );
    }
  };

  const handleCrossDivisionPlayerChange = (id: number, playerId: string) => {
    const selection = crossDivisionSelections.find((s) => s.id === id);

    // Remove old player if exists
    if (selection && selection.playerId) {
      setSelectedPlayers((prev) =>
        prev.filter((pid) => pid !== selection.playerId),
      );
    }

    // Update selection
    setCrossDivisionSelections(
      crossDivisionSelections.map((s) =>
        s.id === id ? { ...s, playerId } : s,
      ),
    );

    // Add new player if selected
    if (playerId) {
      setSelectedPlayers((prev) => [...prev, playerId]);
    }
  };

  const getAvailablePlayersForDivision = (
    division: Division | "",
  ): Player[] => {
    if (!division) return [];
    return allPlayers.filter((p) => p.division === division && p.isActive);
  };

  const handleCreateLeagueDay = () => {
    setSelectedPlayers([]);
    setSelectedDate(new Date());
    setSelectedTime("");
    setLeagueDayName("");
    setAssignedCourt(null);
    setSelectedCourtBookingId("");
    setAvailableCourtBookings([]);
    setPlayerAssignments(null);
    setGenerationCount(0);
    setCrossDivisionSelections([]);
    setEditingDraftId(null); // Not editing a draft
    setSelectedVenueId("");
    setDialogOpen(true);
  };

  const handleCreateLeagueDayForDate = (division: Division, dateStr: string) => {
    setCurrentDivision(division);
    setSelectedPlayers([]);
    setSelectedDate(new Date(dateStr + "T12:00:00")); // noon to avoid timezone shifts
    setSelectedTime("");
    setLeagueDayName("");
    setAssignedCourt(null);
    setSelectedCourtBookingId("");
    setAvailableCourtBookings([]);
    setPlayerAssignments(null);
    setGenerationCount(0);
    setCrossDivisionSelections([]);
    setEditingDraftId(null);
    setSelectedVenueId("");
    setDialogOpen(true);
  };

  const handleEditDraft = (leagueDay: LeagueDay) => {
    setSelectedPlayers(leagueDay.selectedPlayers);
    setSelectedDate(new Date(leagueDay.date));
    setSelectedTime(leagueDay.time || "");
    setLeagueDayName(leagueDay.name || "");
    setCourtCount(leagueDay.courtCount);
    setAssignedCourt(leagueDay.assignedCourt ? parseInt(leagueDay.assignedCourt) : null);
    setSelectedCourtBookingId("");
    setPlayerAssignments(null);
    setGenerationCount(0);
    setSelectedVenueId(leagueDay.venueId || "");

    // Reconstruct cross-division selections from any selected players not in the current division
    const divisionPlayerIds = new Set(players.map((p) => p.id));
    const restoredCrossDivision: CrossDivisionSelection[] = (leagueDay.selectedPlayers || [])
      .filter((id) => !divisionPlayerIds.has(id))
      .map((id, index) => {
        const player = allPlayers.find((p) => p.id === id);
        return {
          id: index,
          division: (player?.division as Division | "") || "",
          playerId: id,
        };
      });
    setCrossDivisionSelections(restoredCrossDivision);

    setEditingDraftId(leagueDay.id); // Set the ID of the draft being edited
    setDialogOpen(true);
    // Fetch available court bookings for this date
    if (leagueDay.date) {
      fetchCourtBookingsForDate(leagueDay.date);
    }
  };

  const handlePlayerToggle = (playerId: string) => {
    const maxPlayers = courtCount * 4;
    setSelectedPlayers((prev) => {
      if (prev.includes(playerId)) {
        const newSelection = prev.filter((id) => id !== playerId);
        console.log(
          "Deselected player:",
          playerId,
          "Total selected:",
          newSelection.length,
        );
        return newSelection;
      } else if (prev.length < maxPlayers) {
        const newSelection = [...prev, playerId];
        console.log(
          "Selected player:",
          playerId,
          "Total selected:",
          newSelection.length,
        );
        return newSelection;
      }
      console.log("Max players reached:", maxPlayers);
      return prev;
    });
  };

  const handleRandomSelection = () => {
    const shuffled = [...players].sort(() => Math.random() - 0.5);
    const maxPlayers = courtCount * 4;
    setSelectedPlayers(shuffled.slice(0, maxPlayers).map((p) => p.id));
  };

  const handleOpenRandomGenerator = () => {
    const expectedPlayerCount = courtCount * 4;
    if (selectedPlayers.length !== expectedPlayerCount) {
      setError(`Please select exactly ${expectedPlayerCount} players first`);
      return;
    }

    // Combine players from current division and all cross-division players
    const crossDivisionPlayerIds = crossDivisionSelections
      .filter((s) => s.playerId)
      .map((s) => s.playerId);
    const crossDivisionPlayerObjects = allPlayers.filter((p) =>
      crossDivisionPlayerIds.includes(p.id),
    );
    const allAvailablePlayers = [...players, ...crossDivisionPlayerObjects];
    const selectedPlayerObjects = allAvailablePlayers.filter((p) =>
      selectedPlayers.includes(p.id),
    );
    setRandomDialogOpen(true);
  };

  const handleRandomGenerationComplete = (result: RandomGenerationResult) => {
    setPlayerAssignments(result);
    setRandomDialogOpen(false);
  };

  const handleSaveDraft = async () => {
    const expectedPlayerCount = courtCount * 4;

    // Only validate player selection if not skipping player selection
    if (
      !skipPlayerSelection &&
      selectedPlayers.length !== expectedPlayerCount
    ) {
      setError(
        `Please select exactly ${expectedPlayerCount} players and a date`,
      );
      return;
    }

    if (!selectedDate) {
      setError("Please select a date");
      return;
    }

    try {
      // If editing an existing draft, update it instead of creating a new one
      if (editingDraftId) {
        const response = await apiRequest(
          `/api/league-days/${editingDraftId}/draft`,
          {
            method: "PUT",
            body: JSON.stringify({
              name: leagueDayName || undefined,
              selectedPlayerIds: skipPlayerSelection ? [] : selectedPlayers,
              date: selectedDate.toISOString(),
              time: selectedTime || undefined,
              courtCount: courtCount,
              playerLimit: playerLimit,
              registrationOpen: false,
              assignedCourt: assignedCourt ? String(assignedCourt) : undefined,
              isTrial: isTrialDay,
              scoringFormat: isTrialDay ? 'points' : 'games',
              venueId: selectedVenueId || null,
            }),
          },
        );

        if (response.ok) {
          setDialogOpen(false);
          fetchLeagueDays();
          setError("");
          setPlayerAssignments(null);
          setEditingDraftId(null);
          setSkipPlayerSelection(false);
          setIsTrialDay(false);
        } else {
          const errorData = await response.json();
          setError(errorData.error || "Failed to update draft league day");
        }
      } else {
        // Creating a new draft
        const response = await apiRequest("/api/league-days", {
          method: "POST",
          body: JSON.stringify({
            name: leagueDayName || undefined,
            selectedPlayerIds: skipPlayerSelection ? [] : selectedPlayers,
            date: selectedDate.toISOString(),
            time: selectedTime || undefined,
            courtCount: courtCount,
            division: currentDivision,
            isDraft: true,
            isTrial: isTrialDay,
            scoringFormat: isTrialDay ? 'points' : 'games',
            playerLimit: skipPlayerSelection ? playerLimit : undefined,
            registrationOpen: skipPlayerSelection ? false : undefined,
            assignedCourt: assignedCourt ? String(assignedCourt) : undefined,
            venueId: selectedVenueId || undefined,
          }),
        });

        if (response.ok) {
          const createdLeagueDay = await response.json();

          setDialogOpen(false);
          fetchLeagueDays();
          setError("");
          setPlayerAssignments(null);
          setEditingDraftId(null);
          setSkipPlayerSelection(false);
          setIsTrialDay(false);
        } else {
          const errorData = await response.json();
          setError(errorData.error || "Failed to save draft league day");
        }
      }
    } catch (error) {
      console.error("Error saving draft league day:", error);
      setError("Failed to save draft league day");
    }
  };

  const handleSaveLeagueDay = async () => {
    const expectedPlayerCount = courtCount * 4;
    if (
      selectedPlayers.length !== expectedPlayerCount ||
      !selectedDate ||
      !playerAssignments
    ) {
      setError(
        `Please select exactly ${expectedPlayerCount} players, a date, and complete the random assignment`,
      );
      return;
    }

    try {
      // If we're editing a draft, use the finalize endpoint
      if (editingDraftId) {
        const response = await apiRequest(
          `/api/league-days/${editingDraftId}/finalize`,
          {
            method: "PUT",
            body: JSON.stringify({
              name: leagueDayName || undefined,
              selectedPlayerIds: selectedPlayers,
              date: selectedDate.toISOString(),
              time: selectedTime || undefined,
              courtCount: courtCount,
              playerAssignments: playerAssignments.assignments,
              assignmentGenerationCount: playerAssignments.generationCount,
              isTrial: isTrialDay,
              scoringFormat: isTrialDay ? 'points' : 'games',
              venueId: selectedVenueId || null,
            }),
          },
        );

        if (response.ok) {
          setDialogOpen(false);
          fetchLeagueDays();
          setError("");
          setPlayerAssignments(null);
          setEditingDraftId(null);
        } else {
          const errorData = await response.json();
          setError(errorData.error || "Failed to finalize league day");
        }
      } else {
        // Creating a new league day
        const response = await apiRequest("/api/league-days", {
          method: "POST",
          body: JSON.stringify({
            name: leagueDayName || undefined,
            selectedPlayerIds: selectedPlayers,
            date: selectedDate.toISOString(),
            time: selectedTime || undefined,
            courtCount: courtCount,
            playerAssignments: playerAssignments.assignments,
            assignmentGenerationCount: playerAssignments.generationCount,
            division: currentDivision,
            isTrial: isTrialDay,
            scoringFormat: isTrialDay ? 'points' : 'games',
            venueId: selectedVenueId || undefined,
          }),
        });

        if (response.ok) {
          setDialogOpen(false);
          fetchLeagueDays();
          setError("");
          setPlayerAssignments(null);
          setIsTrialDay(false);
        } else {
          const errorData = await response.json();
          setError(errorData.error || "Failed to create league day");
        }
      }
    } catch (error) {
      console.error("Error creating league day:", error);
      setError("Failed to create league day");
    }
  };

  const handleSaveHistoricalLeagueDay = async (data: {
    date: Date;
    courtCount: number;
    playerAssignments: any[];
    matches: any[];
  }) => {
    try {
      const response = await apiRequest("/api/league-days/historical", {
        method: "POST",
        body: JSON.stringify({
          ...data,
          division: currentDivision,
        }),
      });

      if (response.ok) {
        setHistoricalDialogOpen(false);
        fetchLeagueDays();
        setError("");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to create historical league day");
      }
    } catch (error) {
      console.error("Error creating historical league day:", error);
      setError("Failed to create historical league day");
    }
  };

  const getPlayerName = (playerId: string) => {
    const player = players.find((p) => p.id === playerId);
    return player?.displayName || player?.name || "Unknown";
  };

  const getPlayerByNumber = (leagueDay: LeagueDay, number: number) => {
    const assignment = leagueDay.playerAssignments?.find(
      (a) => a.assignedNumber === number,
    );
    return assignment ? getPlayerName(assignment.playerId) : `Player ${number}`;
  };

  const getRoundProgress = (leagueDay: LeagueDay) => {
    if (!leagueDay.rounds) return { current: 1, total: 4, completed: 0 };

    const totalRounds = leagueDay.rounds.length;
    const completedRounds = leagueDay.rounds.filter(
      (r) => r.isCompleted,
    ).length;
    return {
      current: leagueDay.currentRound || 1,
      total: totalRounds,
      completed: completedRounds,
    };
  };

  // Helper to get guest players for a league day.
  // A player counts as a guest if they have isGuest=true OR if they
  // were not approved for this season (currentSeason !== leagueDay.seasonId).
  const getGuestPlayers = (
    leagueDay: LeagueDay,
  ): { playerId: string; playerName: string }[] => {
    if (!leagueDay.playerAssignments) return [];

    return leagueDay.playerAssignments
      .filter((assignment) => {
        const player = allPlayers.find((p) => p.id === assignment.playerId);
        if (!player) return false;
        if (player.isGuest) return true;
        if (leagueDay.seasonId && player.currentSeason !== leagueDay.seasonId) return true;
        return false;
      })
      .map((assignment) => ({
        playerId: assignment.playerId,
        playerName: assignment.playerName,
      }));
  };

  // Returns players registered to a league day whose primary division doesn't match
  // the league day's division and who weren't explicitly cross-listed by an admin.
  const getOutOfDivisionPlayers = (leagueDay: LeagueDay): { id: string; name: string; division: string }[] => {
    if (!leagueDay.division) return [];

    // Once a draw is created, only flag players who are actually in the draw.
    // Before the draw, flag everyone in selectedPlayers (waitlist excluded — they're
    // not yet confirmed and the admin may still reject them).
    const drawExists = leagueDay.playerAssignments && leagueDay.playerAssignments.length > 0;
    const playerIdsToCheck: string[] = drawExists
      ? leagueDay.playerAssignments!.map((a) => a.playerId)
      : (leagueDay.selectedPlayers || []);

    if (!playerIdsToCheck.length) return [];

    const ldDiv = leagueDay.division as string;
    return playerIdsToCheck.reduce<{ id: string; name: string; division: string }[]>((acc, playerId) => {
      const player = allPlayers.find((p) => p.id === playerId);
      // Skip flagged guests and unapproved-season players (already shown as guests on the card)
      const isUnapprovedForSeason = leagueDay.seasonId && player?.currentSeason !== leagueDay.seasonId;
      if (!player || player.isGuest || isUnapprovedForSeason) return acc;
      const playerDiv = player.division || '';
      const allowed: string[] = player.allowedDivisions || [];
      if (playerDiv !== ldDiv && !allowed.includes(ldDiv)) {
        acc.push({ id: playerId, name: player.name, division: playerDiv });
      }
      return acc;
    }, []);
  };

  // Generate WhatsApp message for guest player
  const generateGuestPlayerMessage = (
    playerName: string,
    leagueDayDate: Date,
  ): string => {
    const formattedDate = formatLeagueDayDateCustom(leagueDayDate, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }, "en-US");

    // Extract first name only
    const firstName = playerName.split(" ")[0];

    return `Hey ${firstName},

I am happy you are playing on ${formattedDate}. Please don't forget that since you aren't registered officially in the SPL and are playing as a guest player, you will have to pay a fee of 20 Euros. You can pay the fee in cash or via PayPal at the end of the matches.

Thanks and see you there ;)`;
  };

  // Copy guest player message to clipboard
  const handleCopyGuestMessage = (playerName: string, leagueDayDate: Date) => {
    const message = generateGuestPlayerMessage(playerName, leagueDayDate);
    navigator.clipboard
      .writeText(message)
      .then(() => {
        setSnackbarMessage(`Message for ${playerName} copied to clipboard!`);
        setSnackbarOpen(true);
      })
      .catch((err) => {
        console.error("Failed to copy message:", err);
        setError("Failed to copy message to clipboard");
      });
  };

  const handleOpenTimeDialog = (leagueDay: LeagueDay) => {
    setLeagueDayForTime(leagueDay);
    setTimeInput(leagueDay.time || "");
    setTimeLimitInput(String(leagueDay.playerLimit ?? 12));
    setTimeNameInput(leagueDay.name || "");
    const rawDate = leagueDay.date instanceof Date ? leagueDay.date : new Date(leagueDay.date as any);
    setTimeDateInput(rawDate.toISOString().split('T')[0]);
    setTimeVenueId(leagueDay.venueId || "");
    setTimeDialogOpen(true);
  };

  const handleSaveTime = async () => {
    if (!leagueDayForTime) return;

    try {
      const requestBody: Record<string, any> = {
        name: timeNameInput || null,
        time: timeInput || null,
        playerLimit: parseInt(timeLimitInput, 10) || 12,
        date: timeDateInput || undefined,
        venueId: timeVenueId || null,
      };
      
      console.log('Sending update request:', requestBody);
      
      const response = await apiRequest(
        `/api/league-days/${leagueDayForTime.id}`,
        {
          method: "PUT",
          body: JSON.stringify(requestBody),
        },
      );

      if (response.ok) {
        const updatedLeagueDay = await response.json();
        console.log('Received updated league day:', updatedLeagueDay);
        
        // Update the league day in the local state
        setLeagueDays(prevDays => 
          prevDays.map(day => 
            day.id === updatedLeagueDay.id ? updatedLeagueDay : day
          )
        );
        
        setTimeDialogOpen(false);
        setLeagueDayForTime(null);
        setTimeInput("");
        setTimeLimitInput("12");
        setTimeNameInput("");
        setTimeDateInput("");
        setTimeVenueId("");
        setSnackbarMessage("Time, name, and player limit updated successfully!");
        setSnackbarOpen(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to update time");
      }
    } catch (error) {
      console.error("Error updating time:", error);
      setError("Failed to update time");
    }
  };

  const handleDeleteLeagueDay = (leagueDay: LeagueDay) => {
    setLeagueDayToDelete(leagueDay);
    setDeleteDialogOpen(true);
  };

  const handleCancelLeagueDay = (leagueDay: LeagueDay) => {
    setLeagueDayToCancel(leagueDay);
    setCancelReason("");
    setCancelDialogOpen(true);
  };

  const confirmCancelLeagueDay = async () => {
    if (!leagueDayToCancel || !cancelReason.trim()) return;

    setCancelling(true);
    try {
      const response = await apiRequest(
        `/api/league-days/${leagueDayToCancel.id}/cancel`,
        {
          method: "PUT",
          body: JSON.stringify({ reason: cancelReason.trim() }),
        },
      );

      if (response.ok) {
        setCancelDialogOpen(false);
        setLeagueDayToCancel(null);
        setError("");
        setSnackbarMessage("League day cancelled");
        setSnackbarOpen(true);
        fetchLeagueDays();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to cancel league day");
        setCancelDialogOpen(false);
      }
    } catch (error) {
      console.error("Error cancelling league day:", error);
      setError("Failed to cancel league day");
      setCancelDialogOpen(false);
    } finally {
      setCancelling(false);
    }
  };

  const confirmDeleteLeagueDay = async () => {
    if (!leagueDayToDelete) return;

    try {
      const response = await apiRequest(
        `/api/league-days/${leagueDayToDelete.id}`,
        {
          method: "DELETE",
        },
      );

      if (response.ok) {
        setDeleteDialogOpen(false);
        setLeagueDayToDelete(null);
        fetchLeagueDays();
        setError("");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to delete league day");
        setDeleteDialogOpen(false);
      }
    } catch (error) {
      console.error("Error deleting league day:", error);
      setError("Failed to delete league day");
      setDeleteDialogOpen(false);
    }
  };

  const handleRecalculateStats = async (leagueDay: LeagueDay) => {
    setLeagueDayToRecalculate(leagueDay);
    setRecalculateDialogOpen(true);
  };

  const handleRegenerateAssignments = (leagueDay: LeagueDay) => {
    setLeagueDayToRegenerate(leagueDay);
    setRegenerateDialogOpen(true);
  };

  const confirmRegenerateAssignments = async () => {
    if (!leagueDayToRegenerate) return;

    setRegenerating(true);
    try {
      const response = await apiRequest(
        `/api/league-days/${leagueDayToRegenerate.id}/regenerate-assignments`,
        { method: "POST" },
      );

      if (response.ok) {
        setError("");
        setSnackbarMessage("Assignments regenerated. All pairings have changed.");
        setSnackbarOpen(true);
        fetchLeagueDays();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to regenerate assignments");
      }
    } catch (error) {
      console.error("Error regenerating assignments:", error);
      setError("Failed to regenerate assignments");
    } finally {
      setRegenerating(false);
      setRegenerateDialogOpen(false);
      setLeagueDayToRegenerate(null);
    }
  };

  const handleShowTrialRanking = async (leagueDay: LeagueDay) => {
    setTrialRankingLeagueDay(leagueDay);
    setTrialRanking([]);
    setTrialRankingDialogOpen(true);

    try {
      // Fetch all completed matches for this league day
      const response = await apiRequest(`/api/matches/league-day/${leagueDay.id}`);
      if (!response.ok) return;
      const matches = await response.json();

      // Build a points map: playerId → total points scored
      const pointsMap: Record<string, number> = {};
      for (const match of matches) {
        if (!match.score?.isCompleted) continue;
        const pair1Players = [match.pair1?.player1Id, match.pair1?.player2Id].filter(Boolean);
        const pair2Players = [match.pair2?.player1Id, match.pair2?.player2Id].filter(Boolean);
        pair1Players.forEach((id: string) => { pointsMap[id] = (pointsMap[id] || 0) + (match.score.pair1Games || 0); });
        pair2Players.forEach((id: string) => { pointsMap[id] = (pointsMap[id] || 0) + (match.score.pair2Games || 0); });
      }

      // Resolve names from playerAssignments
      const nameMap: Record<string, string> = {};
      (leagueDay.playerAssignments || []).forEach((a) => { nameMap[a.playerId] = a.playerName || a.playerId; });

      const ranking = Object.entries(pointsMap)
        .map(([playerId, points]) => ({ playerId, name: nameMap[playerId] || playerId, points }))
        .sort((a, b) => b.points - a.points);

      setTrialRanking(ranking);
    } catch (e) {
      console.error("Failed to load trial ranking", e);
    }
  };

  const confirmRecalculateStats = async () => {
    if (!leagueDayToRecalculate) return;

    try {
      const response = await apiRequest(
        `/api/stats/recalculate/${leagueDayToRecalculate.id}`,
        {
          method: "POST",
        },
      );

      if (response.ok) {
        setRecalculateDialogOpen(false);
        setLeagueDayToRecalculate(null);
        alert("Stats recalculated successfully!");
        setError("");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to recalculate stats");
        setRecalculateDialogOpen(false);
      }
    } catch (error) {
      console.error("Error recalculating stats:", error);
      setError("Failed to recalculate stats");
      setRecalculateDialogOpen(false);
    }
  };

  const handleOpenRegistration = (leagueDay: LeagueDay) => {
    setLeagueDayForRegistration(leagueDay);
    setPlayerLimit(12); // Default player limit
    setOpenRegistrationDialogOpen(true);
  };

  const confirmOpenRegistration = async () => {
    if (!leagueDayForRegistration) return;

    try {
      const response = await apiRequest(
        `/api/league-days/${leagueDayForRegistration.id}/open-registration`,
        {
          method: "PUT",
          body: JSON.stringify({ playerLimit }),
        },
      );

      if (response.ok) {
        setOpenRegistrationDialogOpen(false);
        setLeagueDayForRegistration(null);
        fetchLeagueDays();
        setSnackbarMessage("Registration opened successfully!");
        setSnackbarOpen(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to open registration");
        setOpenRegistrationDialogOpen(false);
      }
    } catch (error) {
      console.error("Error opening registration:", error);
      setError("Failed to open registration");
      setOpenRegistrationDialogOpen(false);
    }
  };

  const handleCloseRegistration = async (leagueDay: LeagueDay) => {
    if (
      !window.confirm(
        "Are you sure you want to close registration for this league day?",
      )
    ) {
      return;
    }

    try {
      const response = await apiRequest(
        `/api/league-days/${leagueDay.id}/close-registration`,
        {
          method: "PUT",
        },
      );

      if (response.ok) {
        fetchLeagueDays();
        setSnackbarMessage("Registration closed successfully!");
        setSnackbarOpen(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to close registration");
      }
    } catch (error) {
      console.error("Error closing registration:", error);
      setError("Failed to close registration");
    }
  };

  const handleScheduleRegistration = (leagueDay: LeagueDay) => {
    setLeagueDayForRegistration(leagueDay);
    setPlayerLimit(leagueDay.playerLimit || 12);
    // Set default to tomorrow at noon
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    setScheduledDateTime(tomorrow);
    setScheduleRegistrationDialogOpen(true);
  };

  const confirmScheduleRegistration = async () => {
    if (!leagueDayForRegistration || !scheduledDateTime) return;

    try {
      const response = await apiRequest(
        `/api/league-days/${leagueDayForRegistration.id}`,
        {
          method: "PUT",
          body: JSON.stringify({
            registrationScheduledFor: scheduledDateTime,
            playerLimit,
          }),
        },
      );

      if (response.ok) {
        setScheduleRegistrationDialogOpen(false);
        setLeagueDayForRegistration(null);
        fetchLeagueDays();
        setSnackbarMessage("Registration scheduled successfully!");
        setSnackbarOpen(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to schedule registration");
        setScheduleRegistrationDialogOpen(false);
      }
    } catch (error) {
      console.error("Error scheduling registration:", error);
      setError("Failed to schedule registration");
      setScheduleRegistrationDialogOpen(false);
    }
  };

  const handleCancelScheduledRegistration = async (leagueDay: LeagueDay) => {
    if (
      !window.confirm(
        "Are you sure you want to cancel the scheduled registration timer? This will remove the automatic registration opening.",
      )
    ) {
      return;
    }

    try {
      const response = await apiRequest(`/api/league-days/${leagueDay.id}`, {
        method: "PUT",
        body: JSON.stringify({
          registrationScheduledFor: null,
        }),
      });

      if (response.ok) {
        fetchLeagueDays();
        setSnackbarMessage(
          "Scheduled registration timer cancelled successfully!",
        );
        setSnackbarOpen(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to cancel scheduled registration");
      }
    } catch (error) {
      console.error("Error cancelling scheduled registration:", error);
      setError("Failed to cancel scheduled registration");
    }
  };

  const handlePrintSchedule = async (leagueDay: LeagueDay) => {
    // Fetch the latest league day data to ensure we have current match pairings
    let currentLeagueDay = leagueDay;
    try {
      const response = await apiRequest(`/api/league-days/${leagueDay.id}`);
      if (response.ok) {
        currentLeagueDay = await response.json();
      }
    } catch (error) {
      console.error("Error fetching latest league day data:", error);
      // Continue with existing data if fetch fails
    }

    // Initialize default court numbers (1, 2, 3, etc.)
    const courtCount = currentLeagueDay.courtCount || 3;
    const defaultCourtNumbers: { [key: number]: string } = {};
    for (let i = 1; i <= courtCount; i++) {
      defaultCourtNumbers[i] = i.toString();
    }
    setCustomCourtNumbers(defaultCourtNumbers);
    setLeagueDayToPrint(currentLeagueDay);
    setCourtNumbersDialogOpen(true);
  };

  const handleConfirmPrint = () => {
    if (!leagueDayToPrint) return;
    setCourtNumbersDialogOpen(false);
    performPrint(leagueDayToPrint, customCourtNumbers);
  };

  const performPrint = (
    currentLeagueDay: LeagueDay,
    courtMapping: { [key: number]: string },
  ) => {
    // Create a new window for printing
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    // Get player name by ID - look up from all players
    const getPlayerName = (playerId: string) => {
      const player = allPlayers.find((p) => p.id === playerId);
      return player?.displayName || player?.name || "Unknown";
    };

    // Get player number by ID from the current assignments
    const getPlayerNumber = (playerId: string) => {
      const assignment = currentLeagueDay.playerAssignments?.find(
        (a) => a.playerId === playerId,
      );
      return assignment?.assignedNumber || "?";
    };

    // Calculate max courts in any round to determine sizing
    const maxCourts = Math.max(
      ...(currentLeagueDay.rounds?.map((r) => r.matches.length) || [0]),
    );

    // Build HTML content with dynamic sizing based on court count
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Padel Court Schedule - {formatLeagueDayDateShort(currentLeagueDay.date)}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 12mm;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              background: white !important;
              color: #333 !important;
            }
            .page {
              page-break-after: always !important;
              page-break-inside: avoid !important;
              display: block !important;
              padding: ${
                maxCourts <= 2
                  ? "10px"
                  : maxCourts <= 3
                    ? "16px"
                    : maxCourts <= 4
                      ? "14px"
                      : "12px"
              } !important;
              position: relative;
            }
            .page:last-child {
              page-break-after: auto !important;
            }
            .header {
              text-align: center !important;
              margin-bottom: ${
                maxCourts <= 2
                  ? "8px"
                  : maxCourts <= 3
                    ? "10px"
                    : maxCourts <= 4
                      ? "8px"
                      : "6px"
              } !important;
              padding-bottom: ${
                maxCourts <= 2
                  ? "6px"
                  : maxCourts <= 3
                    ? "8px"
                    : maxCourts <= 4
                      ? "6px"
                      : "5px"
              } !important;
              border-bottom: 2px solid #1976d2 !important;
            }
            .header .date {
              font-size: ${
                maxCourts <= 2
                  ? "12px"
                  : maxCourts <= 3
                    ? "14px"
                    : maxCourts <= 4
                      ? "13px"
                      : "12px"
              } !important;
              color: #666 !important;
              font-weight: 500 !important;
              margin-bottom: 4px !important;
            }
            .header .division {
              display: inline-block !important;
              background: #1976d2 !important;
              color: white !important;
              padding: ${
                maxCourts <= 2
                  ? "3px 8px"
                  : maxCourts <= 3
                    ? "4px 10px"
                    : maxCourts <= 4
                      ? "3px 10px"
                      : "3px 8px"
              } !important;
              border-radius: 4px !important;
              font-size: ${
                maxCourts <= 2
                  ? "11px"
                  : maxCourts <= 3
                    ? "13px"
                    : maxCourts <= 4
                      ? "12px"
                      : "11px"
              } !important;
              margin-top: 4px !important;
              font-weight: 600 !important;
            }
            .round-title {
              background: #1976d2 !important;
              color: white !important;
              padding: ${
                maxCourts <= 2
                  ? "6px 10px"
                  : maxCourts <= 3
                    ? "8px 12px"
                    : maxCourts <= 4
                      ? "7px 10px"
                      : "6px 8px"
              } !important;
              font-size: ${
                maxCourts <= 2
                  ? "16px"
                  : maxCourts <= 3
                    ? "20px"
                    : maxCourts <= 4
                      ? "18px"
                      : "16px"
              } !important;
              font-weight: bold !important;
              border-radius: 5px !important;
              margin-bottom: ${
                maxCourts <= 2
                  ? "10px"
                  : maxCourts <= 3
                    ? "14px"
                    : maxCourts <= 4
                      ? "12px"
                      : "10px"
              } !important;
              display: flex !important;
              justify-content: space-between !important;
              align-items: center !important;
            }
            .round-subtitle {
              font-size: ${
                maxCourts <= 2
                  ? "12px"
                  : maxCourts <= 3
                    ? "14px"
                    : maxCourts <= 4
                      ? "13px"
                      : "12px"
              } !important;
              font-weight: normal !important;
              opacity: 0.95 !important;
            }
            .courts {
              display: grid !important;
              grid-template-columns: ${
                maxCourts <= 2
                  ? "repeat(2, 1fr)"
                  : maxCourts <= 3
                    ? "repeat(auto-fit, minmax(300px, 1fr))"
                    : maxCourts <= 4
                      ? "repeat(2, 1fr)"
                      : "repeat(2, 1fr)"
              } !important;
              gap: ${
                maxCourts <= 2
                  ? "12px"
                  : maxCourts <= 3
                    ? "14px"
                    : maxCourts <= 4
                      ? "12px"
                      : "8px"
              } !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .court {
              border: 2px solid #1976d2 !important;
              border-radius: 8px !important;
              padding: ${
                maxCourts <= 2
                  ? "10px"
                  : maxCourts <= 3
                    ? "12px"
                    : maxCourts <= 4
                      ? "10px"
                      : "8px"
              } !important;
              background: #f8f9fa !important;
              display: flex !important;
              flex-direction: column !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .court-header {
              background: #1976d2 !important;
              color: white !important;
              padding: ${
                maxCourts <= 2
                  ? "6px 8px"
                  : maxCourts <= 3
                    ? "8px 10px"
                    : maxCourts <= 4
                      ? "6px 8px"
                      : "5px 6px"
              } !important;
              margin: ${
                maxCourts <= 2
                  ? "-10px -10px 10px -10px"
                  : maxCourts <= 3
                    ? "-12px -12px 12px -12px"
                    : maxCourts <= 4
                      ? "-10px -10px 10px -10px"
                      : "-8px -8px 8px -8px"
              } !important;
              border-radius: 6px 6px 0 0 !important;
              font-weight: bold !important;
              font-size: ${
                maxCourts <= 2
                  ? "16px"
                  : maxCourts <= 3
                    ? "19px"
                    : maxCourts <= 4
                      ? "16px"
                      : "14px"
              } !important;
              text-align: center !important;
            }
            .team {
              margin-bottom: ${
                maxCourts <= 2
                  ? "8px"
                  : maxCourts <= 3
                    ? "10px"
                    : maxCourts <= 4
                      ? "8px"
                      : "6px"
              } !important;
              position: relative !important;
            }
            .team-label {
              font-size: ${
                maxCourts <= 2
                  ? "12px"
                  : maxCourts <= 3
                    ? "14px"
                    : maxCourts <= 4
                      ? "12px"
                      : "11px"
              } !important;
              color: #1976d2 !important;
              font-weight: 700 !important;
              text-transform: uppercase !important;
              margin-bottom: ${
                maxCourts <= 2
                  ? "5px"
                  : maxCourts <= 3
                    ? "6px"
                    : maxCourts <= 4
                      ? "5px"
                      : "4px"
              } !important;
              letter-spacing: 0.5px !important;
            }
            .team-content {
              display: flex !important;
              justify-content: space-between !important;
              align-items: center !important;
              gap: ${
                maxCourts <= 2
                  ? "8px"
                  : maxCourts <= 3
                    ? "10px"
                    : maxCourts <= 4
                      ? "8px"
                      : "6px"
              } !important;
            }
            .players {
              flex: 1 !important;
            }
            .player {
              background: white !important;
              padding: ${
                maxCourts <= 2
                  ? "6px 8px"
                  : maxCourts <= 3
                    ? "8px 10px"
                    : maxCourts <= 4
                      ? "6px 8px"
                      : "5px 6px"
              } !important;
              margin: ${
                maxCourts <= 2
                  ? "4px 0"
                  : maxCourts <= 3
                    ? "5px 0"
                    : maxCourts <= 4
                      ? "4px 0"
                      : "3px 0"
              } !important;
              border-radius: 4px !important;
              border-left: 3px solid #1976d2 !important;
              font-size: ${
                maxCourts <= 2
                  ? "13px"
                  : maxCourts <= 3
                    ? "16px"
                    : maxCourts <= 4
                      ? "14px"
                      : "12px"
              } !important;
              font-weight: 500 !important;
              display: flex !important;
              align-items: center !important;
            }
            .player-number {
              background: #1976d2 !important;
              color: white !important;
              min-width: ${
                maxCourts <= 2
                  ? "24px"
                  : maxCourts <= 3
                    ? "28px"
                    : maxCourts <= 4
                      ? "24px"
                      : "20px"
              } !important;
              height: ${
                maxCourts <= 2
                  ? "24px"
                  : maxCourts <= 3
                    ? "28px"
                    : maxCourts <= 4
                      ? "24px"
                      : "20px"
              } !important;
              border-radius: 50% !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
              font-size: ${
                maxCourts <= 2
                  ? "12px"
                  : maxCourts <= 3
                    ? "14px"
                    : maxCourts <= 4
                      ? "12px"
                      : "10px"
              } !important;
              font-weight: bold !important;
              margin-right: ${
                maxCourts <= 2
                  ? "6px"
                  : maxCourts <= 3
                    ? "8px"
                    : maxCourts <= 4
                      ? "6px"
                      : "5px"
              } !important;
              flex-shrink: 0 !important;
            }
            .score-box {
              width: ${
                maxCourts <= 2
                  ? "70px"
                  : maxCourts <= 3
                    ? "60px"
                    : maxCourts <= 4
                      ? "50px"
                      : "45px"
              } !important;
              height: ${
                maxCourts <= 2
                  ? "70px"
                  : maxCourts <= 3
                    ? "60px"
                    : maxCourts <= 4
                      ? "50px"
                      : "45px"
              } !important;
              border: 2px solid #333 !important;
              border-radius: 5px !important;
              background: white !important;
              flex-shrink: 0 !important;
            }
            .vs {
              text-align: center !important;
              color: #1976d2 !important;
              font-weight: bold !important;
              font-size: ${
                maxCourts <= 2
                  ? "24px"
                  : maxCourts <= 3
                    ? "20px"
                    : maxCourts <= 4
                      ? "16px"
                      : "14px"
              } !important;
              margin: ${
                maxCourts <= 2
                  ? "12px 0"
                  : maxCourts <= 3
                    ? "10px 0"
                    : maxCourts <= 4
                      ? "8px 0"
                      : "6px 0"
              } !important;
              letter-spacing: 2px !important;
            }
            .round-section {
              margin-top: 20px !important;
              page-break-inside: avoid !important;
            }
            .rounds-grid {
              display: grid !important;
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 15px !important;
              margin-top: 15px !important;
              page-break-inside: avoid !important;
            }
            .round-card {
              border: 2px solid #1976d2 !important;
              border-radius: 8px !important;
              padding: 12px !important;
              background: #ffffff !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .round-card .round-title {
              margin-bottom: 12px !important;
              font-size: 16px !important;
              padding: 6px 10px !important;
            }
            .round-card .round-subtitle {
              font-size: 12px !important;
            }
            .court-compact {
              border: none !important;
              border-radius: 0 !important;
              padding: 0 !important;
              background: transparent !important;
            }
            .court-compact .court-header {
              background: #1976d2 !important;
              color: white !important;
              padding: 6px 8px !important;
              margin: 0 0 10px 0 !important;
              border-radius: 4px !important;
              font-weight: bold !important;
              font-size: 14px !important;
              text-align: center !important;
            }
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              body {
                padding: 0 !important;
                margin: 0 !important;
              }
              .page {
                page-break-after: always !important;
                page-break-inside: avoid !important;
                padding: 15px !important;
                height: auto;
                min-height: 100vh;
              }
              .page:last-child {
                page-break-after: auto !important;
              }
              .rounds-grid {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              .round-card {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              .court-compact {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              .round-section {
                margin-top: 20px !important;
                page-break-inside: avoid !important;
                page-break-before: avoid !important;
              }
              .header {
                page-break-after: avoid !important;
                page-break-inside: avoid !important;
              }
              .round-title {
                page-break-after: avoid !important;
                page-break-inside: avoid !important;
              }
              .courts {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                display: grid !important;
              }
              .court {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
            }
          </style>
        </head>
        <body>
          ${
            maxCourts === 1
              ? `
            <div class="page">
              <div class="header">
                <div class="date">${formatLeagueDayDateCustom(
                  currentLeagueDay.date,
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                  "en-GB"
                )}</div>
                <div class="division">${currentLeagueDay.division}</div>
              </div>
              
              <div class="rounds-grid">
                ${currentLeagueDay.rounds
                  ?.map(
                    (round, roundIndex) => `
                  <div class="round-card">
                    <div class="round-title">
                      <span>Round ${round.roundNumber}</span>
                      <span class="round-subtitle">Best of 9 Games</span>
                    </div>
                    ${round.matches
                      .sort((a, b) => a.courtNumber - b.courtNumber)
                      .map(
                        (match) => `
                      <div class="court-compact">
                        <div class="court-header">Court ${
                          courtMapping[match.courtNumber] || match.courtNumber
                        }</div>
                        
                        <div class="team">
                          <div class="team-label">Team 1</div>
                          <div class="team-content">
                            <div class="players">
                              <div class="player">
                                <span class="player-number">${getPlayerNumber(
                                  match.pair1.player1Id,
                                )}</span>
                                ${getPlayerName(match.pair1.player1Id)}
                              </div>
                              <div class="player">
                                <span class="player-number">${getPlayerNumber(
                                  match.pair1.player2Id,
                                )}</span>
                                ${getPlayerName(match.pair1.player2Id)}
                              </div>
                            </div>
                            <div class="score-box"></div>
                          </div>
                        </div>
                        
                        <div class="vs">VS</div>
                        
                        <div class="team">
                          <div class="team-label">Team 2</div>
                          <div class="team-content">
                            <div class="players">
                              <div class="player">
                                <span class="player-number">${getPlayerNumber(
                                  match.pair2.player1Id,
                                )}</span>
                                ${getPlayerName(match.pair2.player1Id)}
                              </div>
                              <div class="player">
                                <span class="player-number">${getPlayerNumber(
                                  match.pair2.player2Id,
                                )}</span>
                                ${getPlayerName(match.pair2.player2Id)}
                              </div>
                            </div>
                            <div class="score-box"></div>
                          </div>
                        </div>
                      </div>
                    `,
                      )
                      .join("")}
                  </div>
                `,
                  )
                  .join("")}
              </div>
            </div>
          `
              : currentLeagueDay.rounds
                  ?.map(
                    (round, roundIndex) => `
            <div class="page">
              <div class="header">
                <div class="date">${formatLeagueDayDateCustom(
                  currentLeagueDay.date,
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                  "en-GB"
                )}</div>
                <div class="division">${currentLeagueDay.division}</div>
              </div>
              
              <div class="round-title">
                <span>Round ${round.roundNumber}</span>
                <span class="round-subtitle">${
                  round.roundNumber === 4
                    ? "Best of 9 Games"
                    : "Best of 7 Games"
                }</span>
              </div>
              
              <div class="courts">
                ${round.matches
                  .sort((a, b) => a.courtNumber - b.courtNumber)
                  .map(
                    (match) => `
                  <div class="court">
                    <div class="court-header">Court ${
                      courtMapping[match.courtNumber] || match.courtNumber
                    }</div>
                    
                    <div class="team">
                      <div class="team-label">Team 1</div>
                      <div class="team-content">
                        <div class="players">
                          <div class="player">
                            <span class="player-number">${getPlayerNumber(
                              match.pair1.player1Id,
                            )}</span>
                            ${getPlayerName(match.pair1.player1Id)}
                          </div>
                          <div class="player">
                            <span class="player-number">${getPlayerNumber(
                              match.pair1.player2Id,
                            )}</span>
                            ${getPlayerName(match.pair1.player2Id)}
                          </div>
                        </div>
                        <div class="score-box"></div>
                      </div>
                    </div>
                    
                    <div class="vs">VS</div>
                    
                    <div class="team">
                      <div class="team-label">Team 2</div>
                      <div class="team-content">
                        <div class="players">
                          <div class="player">
                            <span class="player-number">${getPlayerNumber(
                              match.pair2.player1Id,
                            )}</span>
                            ${getPlayerName(match.pair2.player1Id)}
                          </div>
                          <div class="player">
                            <span class="player-number">${getPlayerNumber(
                              match.pair2.player2Id,
                            )}</span>
                            ${getPlayerName(match.pair2.player2Id)}
                          </div>
                        </div>
                        <div class="score-box"></div>
                      </div>
                    </div>
                  </div>
                `,
                  )
                  .join("")}
              </div>
            </div>
          `,
                  )
                  .join("")
          }
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleCopyRegistrationForWhatsApp = async (leagueDay: LeagueDay) => {
    // Get player name by ID - look up from all players
    const getPlayerName = (playerId: string) => {
      const player = allPlayers.find((p) => p.id === playerId);
      return player?.displayName || player?.name || "Unknown";
    };

    // Format date and time
    const date = new Date(leagueDay.date);
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const day = date.getDate();
    const monthIndex = date.getMonth();
    const dayOfWeek = dayNames[date.getDay()];

    const formattedDate = `${day} ${monthNames[monthIndex]}. ${dayOfWeek}.🏅`;
    const timeStr = leagueDay.time || "TBD";

    // Build WhatsApp formatted text
    let whatsappText = `${formattedDate}\n`;
    whatsappText += `${timeStr} - SPL 25/26\n`;
    if (leagueDay.venueName) {
      whatsappText += `📍 ${leagueDay.venueName}\n`;
      if (leagueDay.venueAddress) {
        whatsappText += `${getMapsUrl(leagueDay.venueAddress)}\n`;
      }
    }

    // Add confirmed players
    const confirmedPlayers = leagueDay.selectedPlayers || [];
    confirmedPlayers.forEach((playerId) => {
      const playerName = getPlayerName(playerId);
      whatsappText += `🎾 ${playerName}\n`;
    });

    // Add subs/waitlist if any
    const waitlistPlayers = leagueDay.waitlistPlayerIds || [];
    if (waitlistPlayers.length > 0) {
      whatsappText += `Sub.\n`;
      waitlistPlayers.forEach((playerId) => {
        const playerName = getPlayerName(playerId);
        whatsappText += `🎾 ${playerName}\n`;
      });
    }

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(whatsappText);
      setSnackbarMessage(
        "Registration list copied to clipboard! Ready to paste in WhatsApp.",
      );
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      setSnackbarMessage("Failed to copy to clipboard");
      setSnackbarOpen(true);
    }
  };

  const handleCopyForWhatsApp = async (leagueDay: LeagueDay) => {
    // Fetch the latest league day data to ensure we have current match pairings
    let currentLeagueDay = leagueDay;
    try {
      const response = await apiRequest(`/api/league-days/${leagueDay.id}`);
      if (response.ok) {
        currentLeagueDay = await response.json();
      }
    } catch (error) {
      console.error("Error fetching latest league day data:", error);
      // Continue with existing data if fetch fails
    }

    // Get player name by ID - look up from all players
    const getPlayerName = (playerId: string) => {
      const player = allPlayers.find((p) => p.id === playerId);
      return player?.displayName || player?.name || "Unknown";
    };

    // Get player number by ID from the current assignments
    const getPlayerNumber = (playerId: string) => {
      const assignment = currentLeagueDay.playerAssignments?.find(
        (a) => a.playerId === playerId,
      );
      return assignment?.assignedNumber || "?";
    };

    // Build WhatsApp formatted text - compact version with names
    const date = formatLeagueDayDateCustom(currentLeagueDay.date, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }, "en-GB");

    let whatsappText = `🎾 *${currentLeagueDay.division}* - ${date}\n`;
    if (currentLeagueDay.time) {
      whatsappText += `⏰ ${currentLeagueDay.time}\n`;
    }
    if (currentLeagueDay.venueName) {
      whatsappText += `📍 ${currentLeagueDay.venueName}\n`;
      if (currentLeagueDay.venueAddress) {
        whatsappText += `${getMapsUrl(currentLeagueDay.venueAddress)}\n`;
      }
    }
    whatsappText += `\n`;

    currentLeagueDay.rounds?.forEach((round) => {
      const isTrialRound = currentLeagueDay.isTrial || currentLeagueDay.scoringFormat === 'points';
      whatsappText += `*━━━ Round ${round.roundNumber} ━━━*\n${
        isTrialRound ? "Points Based\n" : (round.roundNumber === 4 ? "Best of 9 Games\n" : "Best of 7 Games\n")
      }`;

      round.matches
        .sort((a, b) => a.courtNumber - b.courtNumber)
        .forEach((match) => {
          const p1 = getPlayerName(match.pair1.player1Id);
          const p2 = getPlayerName(match.pair1.player2Id);
          const p3 = getPlayerName(match.pair2.player1Id);
          const p4 = getPlayerName(match.pair2.player2Id);

          whatsappText += `*Court ${match.courtNumber}*\n`;
          whatsappText += `${p1} + ${p2}  🆚  ${p3} + ${p4}\n`;
        });

      whatsappText += `\n`;
    });

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(whatsappText);
      setSnackbarMessage(
        "Schedule copied to clipboard! Ready to paste in WhatsApp.",
      );
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      setSnackbarMessage("Failed to copy to clipboard");
      setSnackbarOpen(true);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography variant="h4">League Days</Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: { xs: "center", sm: "flex-end" },
          }}
        >
          <Button
            variant="text"
            startIcon={<HistoryIcon />}
            onClick={() => setHistoricalDialogOpen(true)}
            disabled={players.length < 8}
            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            Add Historical match Data
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateLeagueDay}
            disabled={players.length < 8}
            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            Create League Day
          </Button>
        </Box>
      </Box>

      {/* View mode toggle */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_e, v) => {
            if (v) {
              setViewMode(v);
              if (v === "date" && !dateViewFilter) {
                setDateViewFilter(new Date().toISOString().slice(0, 10));
              }
            }
          }}
          size="small"
        >
          <ToggleButton value="division">
            <GroupIcon sx={{ mr: 0.5, fontSize: 18 }} /> By Division
          </ToggleButton>
          <ToggleButton value="date">
            <CalendarIcon sx={{ mr: 0.5, fontSize: 18 }} /> By Date
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Division Selector – division mode only */}
      {viewMode === "division" && (
        <Box sx={{ mb: 3 }}>
          <Card
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid rgba(25, 118, 210, 0.1)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "#1976d2",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                🏆 Managing Division: {currentDivision}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <SeasonSelector fullWidth={true} excludeDrafts={true} />
                <DivisionSelector />
              </Box>
            </Box>
          </Card>
        </Box>
      )}

      {/* Date mode – date picker + cross-division results */}
      {viewMode === "date" && (
        <>
          <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              type="date"
              label="Select date"
              size="small"
              value={dateViewFilter}
              onChange={(e) => setDateViewFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <SeasonSelector fullWidth={false} excludeDrafts={true} />
            {dateViewFilter && (
              <Typography variant="body2" color="text.secondary">
                {new Date(dateViewFilter + "T12:00:00").toLocaleDateString(undefined, {
                  weekday: "long", month: "long", day: "numeric", year: "numeric",
                })}
              </Typography>
            )}
            {dateViewFilter && dateViewLeagueDays.length > 0 && (
              <Tooltip title="Show numbered list of all players participating on this date">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PlayerListIcon />}
                  onClick={() => setPlayerListDialogOpen(true)}
                >
                  Player List
                </Button>
              </Tooltip>
            )}
          </Box>
          {dateViewFilter ? (
            <Box sx={{ mb: 3 }}>
          {dateViewLoading ? (
            <LinearProgress sx={{ borderRadius: 1 }} />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fill, minmax(280px, 1fr))",
                },
                gap: 2,
              }}
            >
              {allDivisions.filter((div) => div !== "unassigned" && div !== "Test").map((div) => {
                const ld = dateViewLeagueDays.find((d) => d.division === div);
                return (
                  <Card
                    key={div}
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      border: ld
                        ? "2px solid rgba(46,125,50,0.6)"
                        : "2px solid rgba(211,47,47,0.5)",
                      background: ld
                        ? "rgba(46,125,50,0.04)"
                        : "rgba(211,47,47,0.03)",
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {div}
                        </Typography>
                        {ld ? (
                          <Chip
                            label={ld.isDraft ? "Draft" : ld.isCompleted ? "Completed" : "Active"}
                            color={ld.isDraft ? "default" : ld.isCompleted ? "success" : "primary"}
                            size="small"
                          />
                        ) : (
                          <Chip label="No league day" size="small" variant="outlined" />
                        )}
                      </Box>
                      {ld ? (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                            📅 {formatLeagueDayDateShort(ld.date)}
                          </Typography>
                          {ld.name && (
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {ld.name}
                            </Typography>
                          )}
                          {ld.time && (
                            <Typography variant="body2" color="text.secondary">
                              🕐 {ld.time}
                            </Typography>
                          )}
                          <VenueBadge
                            venueName={ld.venueName}
                            venueAddress={ld.venueAddress}
                            variant="body2"
                          />
                          <Typography variant="body2" color="text.secondary">
                            👥 {ld.selectedPlayers?.length ?? 0} players · {ld.courtCount} courts
                          </Typography>
                          {ld.isTrial && (
                            <Chip label="Trial" size="small" sx={{ mt: 0.5 }} />
                          )}
                          <Box mt={1.5}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setCurrentDivision(div)}
                            >
                              Switch to {div}
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            No league day scheduled for this date.
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleCreateLeagueDayForDate(div, dateViewFilter)}
                            disabled={players.length < 8 && div === currentDivision}
                            sx={{ mt: 0.5 }}
                          >
                            Create for {div}
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CalendarIcon sx={{ fontSize: 64, mb: 2, color: "text.disabled" }} />
              <Typography variant="h6" color="text.secondary">
                Pick a date above to see all divisions
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Player List Dialog */}
      {(() => {
        // Build the sorted player list when the dialog is open
        const sortedLeagueDays = [...dateViewLeagueDays].sort((a, b) => {
          const ai = allDivisions.indexOf(a.division);
          const bi = allDivisions.indexOf(b.division);
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });

        const playerList: { number: number; name: string; division: string }[] = [];
        let counter = 1;
        sortedLeagueDays.forEach((ld) => {
          (ld.selectedPlayers || []).forEach((playerId) => {
            const player = allPlayers.find((p) => p.id === playerId);
            playerList.push({
              number: counter++,
              name: player?.name || "Unknown",
              division: ld.division,
            });
          });
        });

        const handleCopy = () => {
          const text = playerList
            .map((p) => `${p.number}. ${p.name} (${p.division})`)
            .join("\n");
          navigator.clipboard.writeText(text).catch(() => {});
        };

        const dateLabel = dateViewFilter
          ? new Date(dateViewFilter + "T12:00:00").toLocaleDateString(undefined, {
              weekday: "long", month: "long", day: "numeric", year: "numeric",
            })
          : "";

        return (
          <Dialog
            open={playerListDialogOpen}
            onClose={() => setPlayerListDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
              <PlayerListIcon color="primary" />
              <Box>
                <Typography variant="h6" component="div">Player List</Typography>
                <Typography variant="caption" color="text.secondary">{dateLabel}</Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              {playerList.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">No players found for this date.</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 48 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Division</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {playerList.map((row, idx) => (
                        <TableRow
                          key={idx}
                          sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}
                        >
                          <TableCell sx={{ color: "text.secondary", fontWeight: 600 }}>
                            {row.number}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                          <TableCell>
                            <Chip label={row.division} size="small" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {playerList.length} player{playerList.length !== 1 ? "s" : ""} total
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  startIcon={<CopyIcon />}
                  onClick={handleCopy}
                  size="small"
                  variant="outlined"
                  disabled={playerList.length === 0}
                >
                  Copy List
                </Button>
                <Button onClick={() => setPlayerListDialogOpen(false)} variant="contained" size="small">
                  Close
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
        );
      })()}

      {viewMode === "division" && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {viewMode === "division" && players.length < 8 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          At least 8 players are required to create a league day (minimum 2
          courts). Currently have {players.length} players in roster.
        </Alert>
      )}

      <Box
        sx={{
          display: viewMode === "date" ? "none" : "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fill, minmax(340px, 1fr))",
            md: "repeat(auto-fill, minmax(380px, 1fr))",
          },
          gap: { xs: 2, sm: 3 },
          width: "100%",
        }}
      >
        {[...leagueDays]
          .sort((a, b) => {
            // Unfinished first (closest date first), finished at the end (most recent first)
            if (!!a.isCompleted !== !!b.isCompleted) return a.isCompleted ? 1 : -1;
            const diff = new Date(a.date).getTime() - new Date(b.date).getTime();
            return a.isCompleted ? -diff : diff;
          })
          .map((leagueDay) => {
          if (leagueDay.isCancelled) {
            return (
              <Box key={leagueDay.id} sx={{ width: "100%", height: "100%" }}>
                <Card
                  sx={{
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                    border: "2px solid",
                    borderColor: "error.main",
                  }}
                >
                  <CardContent sx={{ opacity: 0.6 }}>
                    {leagueDay.name && (
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                        {leagueDay.name}
                      </Typography>
                    )}
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CalendarIcon sx={{ color: "text.secondary" }} />
                      <Typography variant="h6" sx={{ textDecoration: "line-through" }}>
                        {formatLeagueDayDateShort(leagueDay.date)}
                      </Typography>
                    </Box>
                    {leagueDay.time && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        🕒 {leagueDay.time}
                      </Typography>
                    )}
                    <VenueBadge
                      venueName={leagueDay.venueName}
                      venueAddress={leagueDay.venueAddress}
                    />
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: "grey.100", borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Reason for cancellation
                      </Typography>
                      <Typography variant="body2">
                        {leagueDay.cancellationReason || "—"}
                      </Typography>
                    </Box>
                  </CardContent>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%) rotate(-15deg)",
                      border: "4px solid",
                      borderColor: "error.main",
                      color: "error.main",
                      borderRadius: 1,
                      px: 2,
                      py: 0.5,
                      fontWeight: 900,
                      fontSize: "1.8rem",
                      letterSpacing: 4,
                      textTransform: "uppercase",
                      bgcolor: "rgba(255,255,255,0.7)",
                      pointerEvents: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Cancelled
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteLeagueDay(leagueDay)}
                    title="Delete League Day"
                    sx={{ position: "absolute", top: 8, right: 8 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Card>
              </Box>
            );
          }
          const progress = getRoundProgress(leagueDay);
          return (
            <Box key={leagueDay.id} sx={{ width: "100%", height: "100%" }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Box display="flex" alignItems="center">
                      <CalendarIcon sx={{ mr: 1, color: "primary.main" }} />
                      <Box>
                        {leagueDay.name && (
                          <Typography
                            variant="subtitle1" 
                            sx={{
                              fontWeight: "bold",
                              fontSize: { xs: "0.95rem", sm: "1.1rem" },
                              mb: 0.5,
                            }}
                          >
                            {leagueDay.name}
                          </Typography>
                        )}
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: { xs: "1rem", sm: "1.25rem" },
                            }}
                          >
                            {formatLeagueDayDateShort(leagueDay.date)}
                          </Typography>
                          {leagueDay.isTrial && (
                            <Chip 
                              label="TRIAL" 
                              color="secondary" 
                              size="small"
                              sx={{ height: "20px", fontSize: "0.7rem" }}
                            />
                          )}
                        </Box>
                        {leagueDay.time && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            🕒 {leagueDay.time}
                          </Typography>
                        )}
                        <VenueBadge
                          venueName={leagueDay.venueName}
                          venueAddress={leagueDay.venueAddress}
                        />
                      </Box>
                    </Box>
                    <Box display="flex" gap={1}>
                      {leagueDay.isCompleted && leagueDay.isTrial && (
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleShowTrialRanking(leagueDay)}
                          title="View Day Ranking"
                        >
                          <SortIcon />
                        </IconButton>
                      )}
                      {leagueDay.isCompleted && !leagueDay.isTrial && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleRecalculateStats(leagueDay)}
                          title="Recalculate Stats"
                        >
                          <RefreshIcon />
                        </IconButton>
                      )}
                      {!leagueDay.isDraft && !leagueDay.isCompleted && (
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleRegenerateAssignments(leagueDay)}
                          title="Regenerate Assignments"
                        >
                          <ShuffleIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color={leagueDay.time ? "default" : "primary"}
                        onClick={() => handleOpenTimeDialog(leagueDay)}
                        title={leagueDay.time ? "Edit Time" : "Add Time"}
                      >
                        {leagueDay.time ? <EditIcon /> : <AddAlarmIcon />}
                      </IconButton>
                      {!leagueDay.isCompleted && (
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleCancelLeagueDay(leagueDay)}
                          title="Cancel League Day"
                        >
                          <CancelDayIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteLeagueDay(leagueDay)}
                        title="Delete League Day"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Players: {leagueDay.selectedPlayers.length}
                    </Typography>
                    {leagueDay.isTrial && (
                      <Typography variant="body2" color="secondary.main" sx={{ fontWeight: 'medium' }}>
                        ⚠️ Point-based scoring (no stats tracking)
                      </Typography>
                    )}
                    {(() => {
                      const outOfDiv = getOutOfDivisionPlayers(leagueDay);
                      if (!outOfDiv.length) return null;
                      return (
                        <Box
                          sx={{
                            mt: 0.5,
                            p: 1,
                            bgcolor: "warning.light",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "warning.main",
                          }}
                        >
                          <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                            <WarningIcon sx={{ fontSize: 16 }} />
                            {outOfDiv.length} out-of-division player{outOfDiv.length !== 1 ? "s" : ""}
                          </Typography>
                          {outOfDiv.map((p) => (
                            <Typography key={p.id} variant="caption" color="warning.dark" sx={{ display: "block" }}>
                              {p.name} ({p.division || "no division"})
                            </Typography>
                          ))}
                        </Box>
                      );
                    })()}
                    {leagueDay.assignedCourt && (
                      <Typography variant="body2" color="text.secondary">
                        🏸 Court: {leagueDay.assignedCourt}
                      </Typography>
                    )}
                    {getCourtBookingInfo(leagueDay) && (
                      <Typography 
                        variant="body2" 
                        color="primary.main"
                        sx={{ 
                          fontWeight: 'medium',
                          mt: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        🎾 {getCourtBookingInfo(leagueDay)}
                      </Typography>
                    )}
                    {!leagueDay.isDraft && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Round: {progress.current}/{progress.total} (
                          {progress.completed} completed)
                        </Typography>
                        <Box mt={1}>
                          <LinearProgress
                            variant="determinate"
                            value={(progress.completed / progress.total) * 100}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      </>
                    )}
                    <Chip
                      label={
                        leagueDay.isDraft
                          ? "Draft - Needs Assignment"
                          : leagueDay.isCompleted
                            ? "Completed"
                            : `Round ${progress.current} in Progress`
                      }
                      color={
                        leagueDay.isDraft
                          ? "info"
                          : leagueDay.isCompleted
                            ? "success"
                            : "warning"
                      }
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>

                  {/* Show player assignments with numbers */}
                  {leagueDay.playerAssignments && (
                    <Box mb={2}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Player Numbers:
                        </Typography>
                        {typeof leagueDay.assignmentGenerationCount ===
                          "number" && (
                          <Chip
                            label={
                              leagueDay.assignmentGenerationCount === 1
                                ? "Draw used on 1st attempt"
                                : `Draw used on attempt #${leagueDay.assignmentGenerationCount}`
                            }
                            size="small"
                            color={
                              leagueDay.assignmentGenerationCount === 1
                                ? "success"
                                : "warning"
                            }
                            variant="outlined"
                            title="Number of times the random draw was (re)generated before this assignment was accepted"
                          />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {leagueDay.playerAssignments
                          .sort((a, b) => a.assignedNumber - b.assignedNumber)
                          .slice(0, 6)
                          .map((assignment) => (
                            <Chip
                              key={assignment.playerId}
                              label={`${assignment.assignedNumber}. ${
                                assignment.playerName.split(" ")[0]
                              }`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        {leagueDay.playerAssignments.length > 6 && (
                          <Chip
                            label={`+${
                              leagueDay.playerAssignments.length - 6
                            } more`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Show selected players for drafts (without numbers) */}
                  {leagueDay.isDraft && (
                    <Box mb={2}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {leagueDay.registrationOpen
                          ? `Registered Players (${leagueDay.selectedPlayers?.length || 0}/${leagueDay.playerLimit || 12}):`
                          : "Selected Players:"}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mb: 1,
                        }}
                      >
                        {leagueDay.selectedPlayers
                          .slice(0, 6)
                          .map((playerId) => {
                            const player = players.find(
                              (p) => p.id === playerId,
                            );
                            return (
                              <Chip
                                key={playerId}
                                label={player?.name.split(" ")[0] || "Unknown"}
                                size="small"
                                variant="outlined"
                              />
                            );
                          })}
                        {leagueDay.selectedPlayers.length > 6 && (
                          <Chip
                            label={`+${
                              leagueDay.selectedPlayers.length - 6
                            } more`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      {leagueDay.registrationOpen &&
                        (leagueDay.selectedPlayers.length > 0 ||
                          (leagueDay.waitlistPlayerIds &&
                            leagueDay.waitlistPlayerIds.length > 0)) && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                              setLeagueDayForRegistration(leagueDay);
                              setRegistrationListDialogOpen(true);
                            }}
                            sx={{ mt: 0.5 }}
                          >
                            View Full Registration List
                          </Button>
                        )}
                      {leagueDay.registrationOpen &&
                        leagueDay.waitlistPlayerIds &&
                        leagueDay.waitlistPlayerIds.length > 0 && (
                          <Typography
                            variant="body2"
                            color="warning.main"
                            sx={{ mt: 1 }}
                          >
                            Waitlist: {leagueDay.waitlistPlayerIds.length}{" "}
                            player
                            {leagueDay.waitlistPlayerIds.length !== 1
                              ? "s"
                              : ""}
                          </Typography>
                        )}
                    </Box>
                  )}

                  {/* Waitlist section — shown on draw-created days so admin knows who to call if a spot opens */}
                  {!leagueDay.isDraft &&
                    leagueDay.waitlistPlayerIds &&
                    leagueDay.waitlistPlayerIds.length > 0 && (
                      <Box mb={2}>
                        <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 700, mb: 0.5 }}>
                          Waitlist ({leagueDay.waitlistPlayerIds.length}):
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {leagueDay.waitlistPlayerIds.map((playerId, idx) => {
                            const p = allPlayers.find((pl) => pl.id === playerId);
                            return (
                              <Chip
                                key={playerId}
                                label={`${idx + 1}. ${p?.name || "Unknown"}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                  {/* Guest Players Section */}
                  {!leagueDay.isDraft &&
                    (() => {
                      const guestPlayers = getGuestPlayers(leagueDay);
                      return guestPlayers.length > 0 ? (
                        <Box mb={2}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Guest Players:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            {guestPlayers.map((guest) => (
                              <Box
                                key={guest.playerId}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="body2">
                                  {guest.playerName}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyGuestMessage(
                                      guest.playerName,
                                      leagueDay.date,
                                    )
                                  }
                                  sx={{
                                    color: "#25D366",
                                    "&:hover": {
                                      bgcolor: "rgba(37, 211, 102, 0.1)",
                                    },
                                  }}
                                  title="Copy WhatsApp payment reminder"
                                >
                                  <WhatsAppIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : null;
                    })()}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      gap: 1,
                    }}
                  >
                    {leagueDay.isDraft && (
                      <>
                        {leagueDay.selectedPlayers &&
                          leagueDay.selectedPlayers.length > 0 && (
                            <Button
                              variant="contained"
                              startIcon={
                                <WhatsAppIcon
                                  sx={{ display: { xs: "none", sm: "block" } }}
                                />
                              }
                              onClick={() =>
                                handleCopyRegistrationForWhatsApp(leagueDay)
                              }
                              sx={{
                                minWidth: { xs: "auto", sm: "160px" },
                                height: "auto",
                                minHeight: "36px",
                                py: 1,
                                bgcolor: "#25D366",
                                "&:hover": {
                                  bgcolor: "#1DA851",
                                },
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                px: { xs: 1.5, sm: 2 },
                                whiteSpace: "normal",
                                lineHeight: 1.4,
                                textAlign: "center",
                              }}
                            >
                              <Box
                                component="span"
                                sx={{ display: { xs: "none", sm: "inline" } }}
                              >
                                Copy Registration
                              </Box>
                              <WhatsAppIcon
                                sx={{ display: { xs: "block", sm: "none" } }}
                              />
                            </Button>
                          )}
                        <Button
                          variant="outlined"
                          startIcon={<GroupIcon />}
                          onClick={() => handleEditDraft(leagueDay)}
                          fullWidth
                          sx={{
                            height: "auto",
                            minHeight: "36px",
                            py: 1,
                            fontSize: { xs: "0.875rem", sm: "0.875rem" },
                            whiteSpace: "normal",
                            lineHeight: 1.4,
                            textAlign: "center",
                          }}
                        >
                          Generate Assignments
                        </Button>
                        {leagueDay.registrationOpen ? (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleCloseRegistration(leagueDay)}
                            fullWidth
                            sx={{
                              height: "auto",
                              minHeight: "36px",
                              py: 1,
                              fontSize: { xs: "0.875rem", sm: "0.875rem" },
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              lineHeight: 1.4,
                              textAlign: "center",
                            }}
                          >
                            Close Registration (
                            {leagueDay.selectedPlayers?.length || 0}/
                            {leagueDay.playerLimit || 12})
                          </Button>
                        ) : leagueDay.registrationScheduledFor ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            <Button
                              variant="outlined"
                              color="info"
                              onClick={() =>
                                handleScheduleRegistration(leagueDay)
                              }
                              fullWidth
                              sx={{
                                height: "auto",
                                minHeight: "36px",
                                py: 1.5,
                                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                lineHeight: 1.4,
                                textAlign: "center",
                              }}
                            >
                              Scheduled:{" "}
                              {leagueDay.registrationScheduledFor
                                ? (() => {
                                    try {
                                      const date =
                                        typeof leagueDay.registrationScheduledFor ===
                                        "string"
                                          ? new Date(
                                              leagueDay.registrationScheduledFor,
                                            )
                                          : leagueDay.registrationScheduledFor;
                                      return date.toLocaleString();
                                    } catch {
                                      return "Invalid Date";
                                    }
                                  })()
                                : "Unknown"}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() =>
                                handleCancelScheduledRegistration(leagueDay)
                              }
                              fullWidth
                              sx={{
                                height: "auto",
                                minHeight: "32px",
                                py: 0.75,
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                whiteSpace: "normal",
                                lineHeight: 1.4,
                                textAlign: "center",
                              }}
                            >
                              Cancel Timer
                            </Button>
                          </Box>
                        ) : (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              onClick={() => handleOpenRegistration(leagueDay)}
                              fullWidth
                              sx={{
                                height: "auto",
                                minHeight: "36px",
                                py: 1,
                                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                                whiteSpace: "normal",
                                lineHeight: 1.4,
                                textAlign: "center",
                              }}
                            >
                              Open Registration
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<AddAlarmIcon />}
                              onClick={() =>
                                handleScheduleRegistration(leagueDay)
                              }
                              fullWidth
                              sx={{
                                height: "auto",
                                minHeight: "36px",
                                py: 1,
                                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                                whiteSpace: "normal",
                                lineHeight: 1.4,
                                textAlign: "center",
                              }}
                            >
                              Schedule Registration
                            </Button>
                          </>
                        )}
                      </>
                    )}
                    {!leagueDay.isDraft && (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={<SportsIcon />}
                          onClick={() =>
                            navigate(`/admin/matches/${leagueDay.id}`)
                          }
                          fullWidth
                          sx={{
                            height: "auto",
                            minHeight: "36px",
                            py: 1,
                            fontSize: { xs: "0.875rem", sm: "0.875rem" },
                            whiteSpace: "normal",
                            lineHeight: 1.4,
                            textAlign: "center",
                          }}
                        >
                          View & Score Matches
                        </Button>
                      </>
                    )}
                    {!leagueDay.isDraft && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        <Button
                          variant="contained"
                          startIcon={
                            <WhatsAppIcon
                              sx={{ display: { xs: "none", sm: "block" } }}
                            />
                          }
                          onClick={() => handleCopyForWhatsApp(leagueDay)}
                          sx={{
                            minWidth: { xs: "auto", sm: "140px" },
                            height: "auto",
                            minHeight: "36px",
                            py: 1,
                            flex: { xs: 1, sm: "0 0 auto" },
                            bgcolor: "#25D366",
                            "&:hover": {
                              bgcolor: "#1DA851",
                            },
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            px: { xs: 1.5, sm: 2 },
                            whiteSpace: "normal",
                            lineHeight: 1.4,
                            textAlign: "center",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{ display: { xs: "none", sm: "inline" } }}
                          >
                            WhatsApp
                          </Box>
                          <WhatsAppIcon
                            sx={{ display: { xs: "block", sm: "none" } }}
                          />
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={
                            <PrintIcon
                              sx={{ display: { xs: "none", sm: "block" } }}
                            />
                          }
                          onClick={() => handlePrintSchedule(leagueDay)}
                          sx={{
                            minWidth: { xs: "auto", sm: "140px" },
                            height: "auto",
                            minHeight: "36px",
                            py: 1,
                            flex: { xs: 1, sm: "0 0 auto" },
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            px: { xs: 1.5, sm: 2 },
                            whiteSpace: "normal",
                            lineHeight: 1.4,
                            textAlign: "center",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{ display: { xs: "none", sm: "inline" } }}
                          >
                            Print
                          </Box>
                          <PrintIcon
                            sx={{ display: { xs: "block", sm: "none" } }}
                          />
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>

      {viewMode === "division" && leagueDays.length === 0 && !loading && (
        <Card sx={{ p: 4, textAlign: "center", mt: 3 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No League Days Created Yet
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create your first league day to start organizing matches with the
            structured 4-round tournament format.
          </Typography>
        </Card>
      )}

      {/* Create League Day Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingDraftId(null);
          setIsTrialDay(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingDraftId ? "Edit Draft League Day" : "Create New League Day"}
        </DialogTitle>
        <DialogContent>
          {/* Date Selection */}
          <Box mb={3}>
            <TextField
              label="League Day Date"
              type="date"
              value={
                selectedDate ? selectedDate.toISOString().split("T")[0] : ""
              }
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Time (Optional)"
              type="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              helperText="Optional - You can add this later if needed"
            />
            <TextField
              label="League Day Name (Optional)"
              value={leagueDayName}
              onChange={(e) => setLeagueDayName(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="e.g., Championship Day, Trial Session, etc."
              helperText="Optional - Give this league day a custom name"
            />
            <Box mt={1}>
              <VenueSelector
                value={selectedVenueId}
                onChange={setSelectedVenueId}
                fullWidth
              />
            </Box>
          </Box>

          {/* Court Booking Selection */}
          {availableCourtBookings.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Available Court Bookings
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select Court Booking (Optional)</InputLabel>
                <Select
                  value={selectedCourtBookingId}
                  label="Select Court Booking (Optional)"
                  onChange={(e) => {
                    setSelectedCourtBookingId(e.target.value);
                    // Reset assigned court when booking changes
                    setAssignedCourt(null);
                  }}
                >
                  <MenuItem value="">
                    <em>No court booking</em>
                  </MenuItem>
                  {availableCourtBookings.map((booking) => (
                    <MenuItem key={booking.id} value={booking.id}>
                      {booking.startTime} - {booking.endTime} | Courts:{" "}
                      {booking.courtsAvailable.join(", ")}
                      {booking.status !== "available" && ` (${booking.status})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Assign specific court from booking */}
              {selectedCourtBookingId && (
                <Box mt={2}>
                  <FormControl fullWidth>
                    <InputLabel>Assign Specific Court (Optional)</InputLabel>
                    <Select
                      value={assignedCourt || ""}
                      label="Assign Specific Court (Optional)"
                      onChange={(e) => setAssignedCourt(Number(e.target.value))}
                    >
                      <MenuItem value="">
                        <em>No specific court</em>
                      </MenuItem>
                      {availableCourtBookings
                        .find((b) => b.id === selectedCourtBookingId)
                        ?.courtsAvailable.map((court: number) => {
                          const isAssigned = availableCourtBookings
                            .find((b) => b.id === selectedCourtBookingId)
                            ?.assignments?.some(
                              (a: any) => a.courtNumber === court
                            );
                          return (
                            <MenuItem
                              key={court}
                              value={court}
                              disabled={isAssigned}
                            >
                              Court {court}
                              {isAssigned && " (Already assigned)"}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}

          {/* Court Selection */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              Number of Courts Available
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Courts</InputLabel>
              <Select
                value={courtCount}
                label="Courts"
                onChange={(e) => {
                  setCourtCount(Number(e.target.value));
                  // Keep selected players when court count changes
                  setPlayerAssignments(null); // Reset assignments only
                }}
              >
                <MenuItem value={1}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>1 Court</span>
                    <Chip label="4 Players" size="small" />
                  </Box>
                </MenuItem>
                <MenuItem value={2}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>2 Courts</span>
                    <Chip label="8 Players" size="small" />
                  </Box>
                </MenuItem>
                <MenuItem value={3}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>3 Courts</span>
                    <Chip label="12 Players" size="small" />
                  </Box>
                </MenuItem>
                <MenuItem value={4}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>4 Courts</span>
                    <Chip label="16 Players" size="small" />
                  </Box>
                </MenuItem>
                <MenuItem value={5}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>5 Courts</span>
                    <Chip label="20 Players" size="small" />
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {courtCount} courts selected - Choose exactly {courtCount * 4}{" "}
              players
            </Typography>
          </Box>

          {/* Skip Player Selection Option */}
          <Box mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipPlayerSelection}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSkipPlayerSelection(e.target.checked);
                    if (e.target.checked) {
                      setSelectedPlayers([]); // Clear selected players if skipping
                      setPlayerAssignments(null);
                    }
                  }}
                />
              }
              label="Create draft without players (players will register themselves)"
            />
          </Box>

          {/* Trial Day Option */}
          <Box mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isTrialDay}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setIsTrialDay(e.target.checked);
                  }}
                />
              }
              label="Trial Day (6 rounds, point-based scoring, no stats updates)"
            />
            {isTrialDay && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                Trial days use 6 rounds with different partnerships each round. 
                Matches are scored by points (not games). Stats won't affect player rankings.
              </Typography>
            )}
          </Box>

          {/* Player Selection */}
          {!skipPlayerSelection && (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">
                  Select Players ({selectedPlayers.length}/{courtCount * 4})
                </Typography>
                <Button variant="outlined" onClick={handleRandomSelection}>
                  Random Selection
                </Button>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                {[...players]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((player) => (
                    <Card
                      key={player.id}
                      sx={{
                        cursor: "pointer",
                        border: selectedPlayers.includes(player.id) ? 2 : 1,
                        borderColor: selectedPlayers.includes(player.id)
                          ? "primary.main"
                          : "grey.300",
                        "&:hover": { backgroundColor: "action.hover" },
                        minWidth: "150px",
                        flex: "1 1 150px",
                      }}
                      onClick={() => handlePlayerToggle(player.id)}
                    >
                      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Typography
                          variant="body2"
                          fontWeight={
                            selectedPlayers.includes(player.id)
                              ? "bold"
                              : "normal"
                          }
                        >
                          {player.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {player.totalPoints} pts
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
              </Box>

              {/* Cross-Division Player Selection */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Add Players from Other Divisions (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  If you don't have enough players from {currentDivision}, you
                  can add players from other divisions
                </Typography>

                {crossDivisionSelections.map((selection, index) => (
                  <Box
                    key={selection.id}
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    <FormControl sx={{ minWidth: 200 }}>
                      <InputLabel>Division</InputLabel>
                      <Select
                        value={selection.division}
                        label="Division"
                        onChange={(e) =>
                          handleCrossDivisionDivisionChange(
                            selection.id,
                            e.target.value as Division | "",
                          )
                        }
                      >
                        <MenuItem value="">
                          <em>Select Division</em>
                        </MenuItem>
                        {allDivisions
                          .filter((div) => div !== currentDivision && div !== 'All')
                          .map((div) => (
                            <MenuItem key={div} value={div}>
                              {div}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>

                    <FormControl sx={{ flex: 1 }}>
                      <InputLabel>Player</InputLabel>
                      <Select
                        value={selection.playerId}
                        label="Player"
                        disabled={!selection.division}
                        onChange={(e) =>
                          handleCrossDivisionPlayerChange(
                            selection.id,
                            e.target.value,
                          )
                        }
                      >
                        <MenuItem value="">
                          <em>Select Player</em>
                        </MenuItem>
                        {getAvailablePlayersForDivision(selection.division)
                          .filter(
                            (p) =>
                              !selectedPlayers.includes(p.id) ||
                              p.id === selection.playerId,
                          )
                          .map((player) => (
                            <MenuItem key={player.id} value={player.id}>
                              {player.name} ({player.totalPoints} pts)
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>

                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleCrossDivisionRemove(selection.id)}
                      sx={{ minWidth: "auto", p: 1 }}
                    >
                      <DeleteIcon />
                    </Button>
                  </Box>
                ))}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleCrossDivisionAdd}
                  sx={{ mt: 2 }}
                  disabled={selectedPlayers.length >= courtCount * 4}
                >
                  Add Player from Another Division
                </Button>
              </Box>

              {/* Random Assignment Result */}
              {playerAssignments && (
                <Box mt={3}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Random assignment completed! Players have been assigned
                    numbers 1-{courtCount * 4}.
                  </Alert>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {playerAssignments.assignments
                      .sort((a, b) => a.assignedNumber - b.assignedNumber)
                      .map((assignment) => (
                        <Chip
                          key={assignment.playerId}
                          label={`${assignment.assignedNumber}. ${assignment.playerName}`}
                          color="primary"
                          variant="filled"
                        />
                      ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setEditingDraftId(null);
            }}
          >
            Cancel
          </Button>
          {!playerAssignments ? (
            <>
              <Button
                onClick={handleSaveDraft}
                variant="outlined"
                disabled={
                  (!skipPlayerSelection &&
                    selectedPlayers.length !== courtCount * 4) ||
                  !selectedDate
                }
                startIcon={<CalendarIcon />}
              >
                Save as Draft
              </Button>
              <Button
                onClick={handleOpenRandomGenerator}
                variant="contained"
                disabled={
                  skipPlayerSelection ||
                  selectedPlayers.length !== courtCount * 4 ||
                  !selectedDate
                }
                startIcon={<GroupIcon />}
              >
                Generate Fair Random Assignment
              </Button>
            </>
          ) : (
            <Button
              onClick={handleSaveLeagueDay}
              variant="contained"
              disabled={
                selectedPlayers.length !== courtCount * 4 ||
                !selectedDate ||
                !playerAssignments
              }
            >
              {editingDraftId ? "Finalize League Day" : "Create League Day"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Random Generator Dialog */}
      <RandomGeneratorDialog
        open={randomDialogOpen}
        players={(() => {
          const crossDivisionPlayerIds = crossDivisionSelections
            .filter((s) => s.playerId)
            .map((s) => s.playerId);
          const crossDivisionPlayerObjects = allPlayers.filter((p) =>
            crossDivisionPlayerIds.includes(p.id),
          );
          return [...players, ...crossDivisionPlayerObjects].filter((p) =>
            selectedPlayers.includes(p.id),
          );
        })()}
        courtCount={courtCount}
        division={currentDivision}
        isTrial={isTrialDay}
        generationCount={generationCount}
        onGenerate={() => setGenerationCount((count) => count + 1)}
        onClose={() => setRandomDialogOpen(false)}
        onComplete={handleRandomGenerationComplete}
      />

      {/* Historical League Day Dialog */}
      <HistoricalLeagueDayDialog
        open={historicalDialogOpen}
        players={players}
        onClose={() => setHistoricalDialogOpen(false)}
        onSave={handleSaveHistoricalLeagueDay}
      />

      {/* Trial Day Ranking Dialog */}
      <Dialog
        open={trialRankingDialogOpen}
        onClose={() => setTrialRankingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SortIcon color="secondary" />
          Trial Day Rankings
          {trialRankingLeagueDay && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 1 }}>
              — {formatLeagueDayDateShort(trialRankingLeagueDay.date)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {trialRanking.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              Loading rankings…
            </Typography>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total points scored across all matches. Does not affect official rankings.
              </Typography>
              {trialRanking.map((entry, index) => (
                <Box
                  key={entry.playerId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: 1,
                    px: 1.5,
                    mb: 0.5,
                    borderRadius: 1,
                    bgcolor: index === 0 ? "rgba(255,215,0,0.15)" : index === 1 ? "rgba(192,192,192,0.15)" : index === 2 ? "rgba(205,127,50,0.15)" : "action.hover",
                    border: index < 3 ? "1px solid" : "none",
                    borderColor: index === 0 ? "rgba(255,215,0,0.5)" : index === 1 ? "rgba(192,192,192,0.5)" : "rgba(205,127,50,0.5)",
                  }}
                >
                  <Typography variant="h6" sx={{ minWidth: 32, fontWeight: "bold", color: index === 0 ? "#b8860b" : index === 1 ? "#666" : index === 2 ? "#8b4513" : "text.primary" }}>
                    {index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : `${index + 1}.`}
                  </Typography>
                  <Typography variant="body1" sx={{ flex: 1, fontWeight: index < 3 ? 700 : 400 }}>
                    {entry.name}
                  </Typography>
                  <Chip label={`${entry.points} pts`} size="small" color={index === 0 ? "warning" : "default"} />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrialRankingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Recalculate Stats Confirmation Dialog */}
      <Dialog
        open={recalculateDialogOpen}
        onClose={() => setRecalculateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RefreshIcon color="primary" />
          Recalculate Statistics
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              This will recalculate all player statistics for this league day.
            </Typography>
            <Typography variant="body2">The system will:</Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>Reverse existing statistics for this league day</li>
              <li>Recalculate stats from all completed matches</li>
              <li>Update player totals (games won/lost, points, etc.)</li>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Use this if rankings are not showing correct scores.
            </Typography>
          </Alert>
          {leagueDayToRecalculate && (
            <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body1" gutterBottom>
                <strong>League Day:</strong>{" "}
                {formatLeagueDayDateShort(leagueDayToRecalculate.date)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Division:</strong> {leagueDayToRecalculate.division}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Players:</strong>{" "}
                {leagueDayToRecalculate.selectedPlayers.length}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecalculateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmRecalculateStats}
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
          >
            Recalculate Stats
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Assignments Confirmation Dialog */}
      <Dialog
        open={regenerateDialogOpen}
        onClose={() => !regenerating && setRegenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShuffleIcon color="secondary" />
          Regenerate Assignments
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              This redraws the player numbers for this league day.
            </Typography>
            <Typography variant="body2">
              Every round's partners, opponents and courts will change. Anything
              already shared (WhatsApp, printed schedule) will be out of date.
              This is only possible while no scores have been entered.
            </Typography>
          </Alert>
          {leagueDayToRegenerate && (
            <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body1" gutterBottom>
                <strong>League Day:</strong>{" "}
                {formatLeagueDayDateShort(leagueDayToRegenerate.date)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Division:</strong> {leagueDayToRegenerate.division}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Players:</strong>{" "}
                {leagueDayToRegenerate.selectedPlayers.length}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRegenerateDialogOpen(false)}
            disabled={regenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmRegenerateAssignments}
            variant="contained"
            color="secondary"
            startIcon={<ShuffleIcon />}
            disabled={regenerating}
          >
            {regenerating ? "Regenerating..." : "Regenerate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel League Day Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CancelDayIcon color="warning" />
          Cancel League Day
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              All registered players and the waitlist will be removed, and any
              generated matches deleted. The league day will disappear for
              players and the public site, and stay visible here marked as
              cancelled.
            </Typography>
          </Alert>
          {leagueDayToCancel && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>{formatLeagueDayDateShort(leagueDayToCancel.date)}</strong>{" "}
              · {leagueDayToCancel.division} ·{" "}
              {leagueDayToCancel.selectedPlayers.length} registered player(s)
            </Typography>
          )}
          <TextField
            autoFocus
            required
            fullWidth
            multiline
            minRows={2}
            label="Reason for cancellation"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            Back
          </Button>
          <Button
            onClick={confirmCancelLeagueDay}
            variant="contained"
            color="warning"
            disabled={!cancelReason.trim() || cancelling}
            startIcon={<CancelDayIcon />}
          >
            Cancel League Day
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="error" />
          Delete League Day
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              This action is irreversible!
            </Typography>
            <Typography variant="body2">
              Deleting this league day will permanently remove:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>All match data and scores</li>
              <li>Player assignments and pairings</li>
              <li>Round information</li>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              ✓ Player statistics will be automatically recalculated to reflect
              the deletion.
            </Typography>
          </Alert>
          {leagueDayToDelete && (
            <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body1" gutterBottom>
                <strong>League Day:</strong>{" "}
                {formatLeagueDayDateShort(leagueDayToDelete.date)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Division:</strong> {leagueDayToDelete.division}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Players:</strong>{" "}
                {leagueDayToDelete.selectedPlayers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong>{" "}
                {leagueDayToDelete.isCompleted ? "Completed" : "In Progress"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteLeagueDay}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete League Day
          </Button>
        </DialogActions>
      </Dialog>

      {/* Open Registration Dialog */}
      <Dialog
        open={openRegistrationDialogOpen}
        onClose={() => setOpenRegistrationDialogOpen(false)}
      >
        <DialogTitle>Open Registration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set the player limit for this league day. Players will be able to
            register until the limit is reached.
          </Typography>
          <TextField
            label="Player Limit"
            type="number"
            value={playerLimit}
            onChange={(e) => setPlayerLimit(parseInt(e.target.value) || 12)}
            fullWidth
            inputProps={{ min: 4, max: 30 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRegistrationDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmOpenRegistration}
            variant="contained"
            color="success"
          >
            Open Registration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Registration Dialog */}
      <Dialog
        open={scheduleRegistrationDialogOpen}
        onClose={() => setScheduleRegistrationDialogOpen(false)}
      >
        <DialogTitle>
          <AddAlarmIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Schedule Registration Opening
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set when registration should automatically open for players.
            Registration will open at the scheduled time.
          </Typography>
          <TextField
            label="Scheduled Date & Time"
            type="datetime-local"
            value={
              scheduledDateTime
                ? new Date(
                    scheduledDateTime.getTime() -
                      scheduledDateTime.getTimezoneOffset() * 60000,
                  )
                    .toISOString()
                    .slice(0, 16)
                : ""
            }
            onChange={(e) => setScheduledDateTime(new Date(e.target.value))}
            fullWidth
            sx={{ mt: 2, mb: 2 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="Player Limit"
            type="number"
            value={playerLimit}
            onChange={(e) => setPlayerLimit(parseInt(e.target.value) || 12)}
            fullWidth
            inputProps={{ min: 4, max: 30 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleRegistrationDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmScheduleRegistration}
            variant="contained"
            color="primary"
            startIcon={<AddAlarmIcon />}
          >
            Schedule Registration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registration List Dialog */}
      <Dialog
        open={registrationListDialogOpen}
        onClose={() => {
          setRegistrationListDialogOpen(false);
          setSelectedPlayerToAdd(null);
          setRegistrationSortType("order");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <GroupIcon sx={{ mr: 1 }} />
              Registration List
            </Box>
            <ToggleButtonGroup
              size="small"
              value={registrationSortType}
              exclusive
              onChange={(e, newSort) =>
                newSort && setRegistrationSortType(newSort)
              }
            >
              <ToggleButton value="order">Order</ToggleButton>
              <ToggleButton value="alphabetical">A-Z</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </DialogTitle>
        <DialogContent>
          {leagueDayForRegistration && (
            <>
              {/* Add Player Section */}
              <Box
                mb={3}
                p={2}
                sx={{ backgroundColor: "action.hover", borderRadius: 1 }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Add Player to Registration
                </Typography>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <Autocomplete
                    options={allPlayers.filter((p) => {
                      const registeredIds = [
                        ...(leagueDayForRegistration.selectedPlayers || []),
                        ...(leagueDayForRegistration.waitlistPlayerIds || []),
                      ];
                      return !registeredIds.includes(p.id) && p.isActive;
                    })}
                    getOptionLabel={(option) =>
                      option.division && option.division !== leagueDayForRegistration.division
                        ? `${option.name} (${option.division})`
                        : option.name
                    }
                    value={selectedPlayerToAdd}
                    onChange={(e, newValue) => setSelectedPlayerToAdd(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Player"
                        size="small"
                      />
                    )}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={addToWaitlist}
                        onChange={(e) => setAddToWaitlist(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Add to Waitlist"
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    disabled={!selectedPlayerToAdd}
                    onClick={async () => {
                      if (!selectedPlayerToAdd || !leagueDayForRegistration)
                        return;
                      try {
                        const response = await apiRequest(
                          `/api/league-days/${leagueDayForRegistration.id}/admin-add-registration`,
                          {
                            method: "POST",
                            body: JSON.stringify({
                              playerId: selectedPlayerToAdd.id,
                              toWaitlist: addToWaitlist,
                            }),
                          },
                        );
                        if (response.ok) {
                          setSelectedPlayerToAdd(null);
                          setAddToWaitlist(false);
                          await fetchLeagueDays();
                          const updatedDay = leagueDays.find(
                            (d) => d.id === leagueDayForRegistration.id,
                          );
                          if (updatedDay)
                            setLeagueDayForRegistration(updatedDay);
                        }
                      } catch (error) {
                        console.error("Error adding player:", error);
                      }
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </Box>

              {/* Confirmed Players */}
              <Box mb={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "success.main" }}
                >
                  Confirmed Players (
                  {leagueDayForRegistration.selectedPlayers?.length || 0}/
                  {leagueDayForRegistration.playerLimit || 12})
                  {registrationSortType === "order" && (
                    <Chip size="small" label="Drag to reorder" sx={{ ml: 2 }} />
                  )}
                </Typography>
                {leagueDayForRegistration.selectedPlayers &&
                leagueDayForRegistration.selectedPlayers.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleConfirmedDragEnd}
                  >
                    <SortableContext
                      items={
                        registrationSortType === "order"
                          ? leagueDayForRegistration.selectedPlayers
                          : []
                      }
                      strategy={verticalListSortingStrategy}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {(() => {
                          const playersList =
                            leagueDayForRegistration.selectedPlayers.map(
                              (playerId, originalIndex) => ({
                                playerId,
                                player: allPlayers.find((p) => p.id === playerId),
                                originalIndex,
                              }),
                            );

                          if (registrationSortType === "alphabetical") {
                            playersList.sort((a, b) => {
                              const nameA = a.player?.name || "";
                              const nameB = b.player?.name || "";
                              return nameA.localeCompare(nameB);
                            });
                          }

                          return playersList.map(
                            (
                              { playerId, player, originalIndex },
                              displayIndex,
                            ) => {
                              const SortableItem = () => {
                                const {
                                  attributes,
                                  listeners,
                                  setNodeRef,
                                  transform,
                                  transition,
                                  isDragging,
                                } = useSortable({
                                  id: playerId,
                                  disabled: registrationSortType !== "order",
                                });

                                const style = {
                                  transform: CSS.Transform.toString(transform),
                                  transition,
                                  opacity: isDragging ? 0.5 : 1,
                                };

                                return (
                                  <Box
                                    ref={setNodeRef}
                                    style={style}
                                    key={playerId}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      p: 1,
                                      backgroundColor: "success.light",
                                      borderRadius: 1,
                                      cursor:
                                        registrationSortType === "order"
                                          ? "grab"
                                          : "default",
                                      "&:active":
                                        registrationSortType === "order"
                                          ? { cursor: "grabbing" }
                                          : {},
                                    }}
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      sx={{ flex: 1 }}
                                    >
                                      {registrationSortType === "order" && (
                                        <Box
                                          {...attributes}
                                          {...listeners}
                                          sx={{
                                            display: "flex",
                                            mr: 1,
                                            cursor: "grab",
                                          }}
                                        >
                                          <DragIcon
                                            sx={{ color: "text.secondary" }}
                                          />
                                        </Box>
                                      )}
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: "bold",
                                          mr: 1,
                                          minWidth: "30px",
                                        }}
                                      >
                                        #
                                        {registrationSortType === "order"
                                          ? originalIndex + 1
                                          : displayIndex + 1}
                                      </Typography>
                                      <Typography variant="body2">
                                        {player?.name || "Unknown Player"}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      {leagueDayForRegistration.waitlistPlayerIds &&
                                        leagueDayForRegistration
                                          .waitlistPlayerIds.length > 0 && (
                                          <IconButton
                                            size="small"
                                            onClick={async () => {
                                              try {
                                                const response =
                                                  await apiRequest(
                                                    `/api/league-days/${leagueDayForRegistration.id}/admin-move-registration`,
                                                    {
                                                      method: "POST",
                                                      body: JSON.stringify({
                                                        playerId,
                                                        toWaitlist: true,
                                                      }),
                                                    },
                                                  );
                                                if (response.ok) {
                                                  await fetchLeagueDays();
                                                  const updatedDay =
                                                    leagueDays.find(
                                                      (d) =>
                                                        d.id ===
                                                        leagueDayForRegistration.id,
                                                    );
                                                  if (updatedDay)
                                                    setLeagueDayForRegistration(
                                                      updatedDay,
                                                    );
                                                }
                                              } catch (error) {
                                                console.error(
                                                  "Error moving player:",
                                                  error,
                                                );
                                              }
                                            }}
                                            title="Move to waitlist"
                                          >
                                            <ArrowDownIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={async () => {
                                          if (
                                            window.confirm(
                                              `Remove ${player?.name || "this player"} from registration?`,
                                            )
                                          ) {
                                            try {
                                              const response = await apiRequest(
                                                `/api/league-days/${leagueDayForRegistration.id}/admin-remove-registration/${playerId}`,
                                                { method: "DELETE" },
                                              );
                                              if (response.ok) {
                                                await fetchLeagueDays();
                                                const updatedDay =
                                                  leagueDays.find(
                                                    (d) =>
                                                      d.id ===
                                                      leagueDayForRegistration.id,
                                                  );
                                                if (updatedDay)
                                                  setLeagueDayForRegistration(
                                                    updatedDay,
                                                  );
                                              }
                                            } catch (error) {
                                              console.error(
                                                "Error removing player:",
                                                error,
                                              );
                                            }
                                          }
                                        }}
                                        title="Remove player"
                                      >
                                        <RemoveIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                );
                              };
                              return <SortableItem key={playerId} />;
                            },
                          );
                        })()}
                      </Box>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No confirmed players yet
                  </Typography>
                )}
              </Box>

              {/* Waitlist */}
              {leagueDayForRegistration.waitlistPlayerIds &&
                leagueDayForRegistration.waitlistPlayerIds.length > 0 && (
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ color: "warning.main" }}
                    >
                      Waitlist (
                      {leagueDayForRegistration.waitlistPlayerIds.length})
                      {registrationSortType === "order" && (
                        <Chip
                          size="small"
                          label="Drag to reorder"
                          sx={{ ml: 2 }}
                        />
                      )}
                    </Typography>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleWaitlistDragEnd}
                    >
                      <SortableContext
                        items={
                          registrationSortType === "order"
                            ? leagueDayForRegistration.waitlistPlayerIds
                            : []
                        }
                        strategy={verticalListSortingStrategy}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {(() => {
                            const waitlistPlayers =
                              leagueDayForRegistration.waitlistPlayerIds.map(
                                (playerId, originalIndex) => ({
                                  playerId,
                                  player: allPlayers.find(
                                    (p) => p.id === playerId,
                                  ),
                                  originalIndex,
                                }),
                              );

                            if (registrationSortType === "alphabetical") {
                              waitlistPlayers.sort((a, b) => {
                                const nameA = a.player?.name || "";
                                const nameB = b.player?.name || "";
                                return nameA.localeCompare(nameB);
                              });
                            }

                            return waitlistPlayers.map(
                              (
                                { playerId, player, originalIndex },
                                displayIndex,
                              ) => {
                                const SortableWaitlistItem = () => {
                                  const {
                                    attributes,
                                    listeners,
                                    setNodeRef,
                                    transform,
                                    transition,
                                    isDragging,
                                  } = useSortable({
                                    id: playerId,
                                    disabled: registrationSortType !== "order",
                                  });

                                  const style = {
                                    transform:
                                      CSS.Transform.toString(transform),
                                    transition,
                                    opacity: isDragging ? 0.5 : 1,
                                  };

                                  return (
                                    <Box
                                      ref={setNodeRef}
                                      style={style}
                                      key={playerId}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: 1,
                                        backgroundColor: "warning.light",
                                        borderRadius: 1,
                                        cursor:
                                          registrationSortType === "order"
                                            ? "grab"
                                            : "default",
                                        "&:active":
                                          registrationSortType === "order"
                                            ? { cursor: "grabbing" }
                                            : {},
                                      }}
                                    >
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        sx={{ flex: 1 }}
                                      >
                                        {registrationSortType === "order" && (
                                          <Box
                                            {...attributes}
                                            {...listeners}
                                            sx={{
                                              display: "flex",
                                              mr: 1,
                                              cursor: "grab",
                                            }}
                                          >
                                            <DragIcon
                                              sx={{ color: "text.secondary" }}
                                            />
                                          </Box>
                                        )}
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: "bold",
                                            mr: 1,
                                            minWidth: "30px",
                                          }}
                                        >
                                          #
                                          {registrationSortType === "order"
                                            ? originalIndex + 1
                                            : displayIndex + 1}
                                        </Typography>
                                        <Typography variant="body2">
                                          {player?.name || "Unknown Player"}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        {(leagueDayForRegistration
                                          .selectedPlayers?.length || 0) <
                                          (leagueDayForRegistration.playerLimit ||
                                            12) && (
                                          <IconButton
                                            size="small"
                                            onClick={async () => {
                                              try {
                                                const response =
                                                  await apiRequest(
                                                    `/api/league-days/${leagueDayForRegistration.id}/admin-move-registration`,
                                                    {
                                                      method: "POST",
                                                      body: JSON.stringify({
                                                        playerId,
                                                        toWaitlist: false,
                                                      }),
                                                    },
                                                  );
                                                if (response.ok) {
                                                  await fetchLeagueDays();
                                                  const updatedDay =
                                                    leagueDays.find(
                                                      (d) =>
                                                        d.id ===
                                                        leagueDayForRegistration.id,
                                                    );
                                                  if (updatedDay)
                                                    setLeagueDayForRegistration(
                                                      updatedDay,
                                                    );
                                                }
                                              } catch (error) {
                                                console.error(
                                                  "Error moving player:",
                                                  error,
                                                );
                                              }
                                            }}
                                            title="Promote to confirmed"
                                          >
                                            <ArrowUpIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={async () => {
                                            if (
                                              window.confirm(
                                                `Remove ${player?.name || "this player"} from waitlist?`,
                                              )
                                            ) {
                                              try {
                                                const response =
                                                  await apiRequest(
                                                    `/api/league-days/${leagueDayForRegistration.id}/admin-remove-registration/${playerId}`,
                                                    { method: "DELETE" },
                                                  );
                                                if (response.ok) {
                                                  await fetchLeagueDays();
                                                  const updatedDay =
                                                    leagueDays.find(
                                                      (d) =>
                                                        d.id ===
                                                        leagueDayForRegistration.id,
                                                    );
                                                  if (updatedDay)
                                                    setLeagueDayForRegistration(
                                                      updatedDay,
                                                    );
                                                }
                                              } catch (error) {
                                                console.error(
                                                  "Error removing player:",
                                                  error,
                                                );
                                              }
                                            }
                                          }}
                                          title="Remove player"
                                        >
                                          <RemoveIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  );
                                };
                                return <SortableWaitlistItem key={playerId} />;
                              },
                            );
                          })()}
                        </Box>
                      </SortableContext>
                    </DndContext>
                  </Box>
                )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRegistrationListDialogOpen(false);
              setSelectedPlayerToAdd(null);
              setRegistrationSortType("order");
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Court Numbers Dialog */}
      <Dialog
        open={courtNumbersDialogOpen}
        onClose={() => setCourtNumbersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <PrintIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Specify Court Numbers
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter the actual court numbers for printing. This will help players
            find the correct courts.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {Object.keys(customCourtNumbers).map((courtKey) => {
              const courtNum = parseInt(courtKey);
              return (
                <Box
                  key={courtNum}
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <Typography sx={{ minWidth: 100 }}>
                    Match Court {courtNum}:
                  </Typography>
                  <TextField
                    type="text"
                    value={customCourtNumbers[courtNum]}
                    onChange={(e) =>
                      setCustomCourtNumbers({
                        ...customCourtNumbers,
                        [courtNum]: e.target.value,
                      })
                    }
                    size="small"
                    sx={{ width: 100 }}
                    placeholder={courtNum.toString()}
                  />
                  <Typography variant="caption" color="text.secondary">
                    (Physical court number)
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCourtNumbersDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPrint}
            variant="contained"
            startIcon={<PrintIcon />}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Time Dialog */}
      <Dialog
        open={timeDialogOpen}
        onClose={() => setTimeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {leagueDayForTime?.time
            ? "Edit Time, Name & Player Limit"
            : "Add Time, Name & Player Limit"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="League Day Name (Optional)"
            value={timeNameInput}
            onChange={(e) => setTimeNameInput(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            placeholder="e.g., Championship Day, Trial Session, etc."
          />
          <TextField
            label="Time"
            type="time"
            value={timeInput}
            onChange={(e) => setTimeInput(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="Player Limit"
            type="number"
            value={timeLimitInput}
            onChange={(e) => setTimeLimitInput(e.target.value)}
            onBlur={(e) => {
              const v = parseInt(e.target.value, 10);
              if (isNaN(v) || v < 1) setTimeLimitInput("1");
              else if (v > 30) setTimeLimitInput("30");
            }}
            fullWidth
            sx={{ mt: 2 }}
            inputProps={{ min: 1, max: 30 }}
            helperText="Changing this will move players between confirmed and waitlist"
          />
          <TextField
            label="Date"
            type="date"
            value={timeDateInput}
            onChange={(e) => setTimeDateInput(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
            helperText="Move league day to a different date (players and data are preserved)"
          />
          <Box sx={{ mt: 2 }}>
            <VenueSelector
              value={timeVenueId}
              onChange={setTimeVenueId}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveTime}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

export default LeagueDays;
